import React, {
    CSSProperties,
    DragEvent,
    DragEventHandler,
    FC,
    forwardRef,
    ReactElement,
    ReactNode,
    Suspense,
    SyntheticEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    useDeferredValue,
    useLayoutEffect,
} from 'react';
import './EditableTable.less';
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";
import type {ColumnFilter, EditableTableFilterChoices, FilterDialogHandle, FilterProps, FilterResult, SortStackEntry} from "@/@types/editableTable";
import FilterDialog, {FILTER_DIALOG_MENU_PORTAL_ID, FILTER_DIALOG_TITLE_ID} from "./FilterDialog/FilterDialog";
import {applyRankFilters, evaluateColumnFilter, extractValueParts, flattenForCell, hasRankFilter, type ExportFn} from "./FilterDialog/filterPredicate";
import Help from '../Help/Help';
import IconButton from "../IconButton/IconButton";
import Icons, {IconSizes, IconType} from "../IconButton/Icons";
import MathUtil from "@/lib/mathUtils";
import Toggles, {ToggleProp} from "./Toggles/Toggles";
import chroma from 'chroma-js';
import i18n from "@/i18n/i18n";
import upFirstLetter from "@/lib/picos/upFirstLetter";
import util from "@/lib/util";
import {exportToXlsx, Row as XlsxRow} from '@/lib/xlsx-export';
import {AnimatePresence, motion} from 'framer-motion';
import type {CancelFunction, VoidBoolCallback, VoidCallback} from "@/@types/functions";
import {confirmError, confirmWarning, confirmWarningOkCancelWithOptions} from "../Confirm";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {isMobileView} from "@/lib/picos/browserFns";
import {MOBILE_WIDTH} from "@/lib/picos/breakpoints";
import {renderToString} from 'react-dom/server';
import {stripHtml} from "string-strip-html";
import {Button} from "@mui/material";
import {Add} from "@mui/icons-material";
import {If} from 'react-conditional-components-renderer';
import EditorCard from "./Card/EditorCard";
import useComponentCache from "@/hooks/useComponentCache";
import useStateWithCallback from "@/hooks/useStateWithCallback";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import {EditableTableRow} from './EditableTableRow';
import {
    measureColumnWidths,
    getCachedWidths,
    setCachedWidths,
    buildCacheKey,
    type MeasureColumn,
} from './measureColumnWidths';
import {measureRenderItemFnWidths} from './measureRenderItemFnWidths';
import {contentExtent} from './contentExtent';
import {buildGridColumnTrack} from './buildGridColumnTrack';
import {contentGrew, contentSignature, type ContentSignature} from './contentSignature';
import {captureScroll, restoreScroll, type ScrollTarget} from './editScroll';
import debounceRender from "@/ui/debounceRender";
import debounce from "debounce";
import Loading from "@/ui/Loading";
import {useLocation} from "wouter";
import {hasInAppNavigation} from "@/lib/navigationHistory";

// Bypass wouter v3's monkey-patched replaceState to update URL cosmetically
// without triggering route re-evaluation (wouter patches the instance method,
// so calling the prototype method directly avoids the interception).
const silentReplaceState = History.prototype.replaceState.bind(window.history);

// Reusable DOM element for extracting text from rendered HTML (faster than stripHtml)
let sortTextDiv: HTMLDivElement | null = null;

function widthsEqual(
    a: Record<string, number> | null | undefined,
    b: Record<string, number>,
): boolean {
    if (!a) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
        if (a[k] !== b[k]) return false;
    }
    return true;
}


export enum Justify {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
    FULL = '100'
}

export type JustifyType = typeof Justify[keyof typeof Justify];

type GetGroupInfoReturn = {
    backgroundColor: string,
    textColor: string,
    help?: ReactNode
}

export type EditableTableCardProps<ItemType, Environment = any> = {
    current?: ItemType,
    newId?: string,
    onCancel?: CancelFunction,
    onOk?: (state: ItemType | SyntheticEvent | any) => void,
    env?: Environment,
    presets?: any,
    keys?: string[],
    dropEvent?: DragEventHandler,
    editable?: boolean,
    embedded?: boolean,
    viewOnly?: boolean,
    isAutosave?: boolean,
    param?: string
}

export type EditableTableRenderItemFn<T> = (item: T, label?: string, isExport?: boolean, width?: string | number) => any;

export type EditableTableCustomAddLabel = {
    icon: IconType, // Icon to use for the button (e.g. Icons.ATTACHMENT)
    label: string, // Label for the button
    displayLabel?: string, // Optional string for display
    param?: string,  // this will be passed to the Add method as a parameter
    color?: string, // color of the icon (optional)
    size?: IconSizes, // size of the icon (optional)
    title?: ReactElement | string,
    /** When provided, the button opens this handler instead of triggering the standard add flow.
     *  Use for sibling actions that should sit visually next to the add buttons (e.g. a help
     *  dialog) without participating in the row-creation lifecycle. */
    onClick?: () => void,
};

export type EditableTableGroupEntry = {
    /** The name of the group */
    name: ReactNode,

    /** The color of the grouping's background */
    backgroundColor: string,

    /** The color of the grouping's font */
    textColor?: string,

    /** Optional help section (just a div, we add the help) */
    help?: ReactNode
}

export type EditableTableExportEntry<T> = {
    /** Required label, must match the label in properties */
    label: string,

    /** Optional label for the CSV export */
    displayLabel?: string,

    /** Function to transform item into a number, string or Date */
    exportFn?: (item: T, field: string, isExport: boolean) => any,

    /** Type of data this column represents (number, string, date, etc.) */
    type?: 'string' | 'number' | 'date' | 'money',

    /** Use the formatted version (usually from a render function attached to the property, as a string */
    useFormatted?: boolean,

    /** If set to true, this property will not be included in the export */
    doNotExport?: boolean,
}

export type EditableTablePropertyEntry<T> = {
    /** Label name */
    label: string,

    /** Color of the text, currently unused */
    color?: string,

    /** If present, use this label instead of the field name */
    displayLabel?: ReactNode,

    /** Disallow dragging of this element */
    disableDrag?: boolean,

    /** Optional label for group membership */
    group?: string,

    /** Hide if set to true */
    hide?: boolean,

    /** Don't show a title for this prop */
    hideLabel?: boolean,

    /** Don't show on small screens */
    hideOnMobile?: boolean,

    justify?: JustifyType,

    /** Label for use when small */
    displayLabelSmall?: string,

    /** Do not export this property */
    doNotExport?: boolean,

    /** Only show this column in an export */
    exportOnly?: boolean,

    /** A click on this field normally edits it, true means not to edit on click */
    noClick?: boolean,

    /** By default, we put margins of auto/0 on all cells. This can mess up formatting for some cells. */
    noCellMargins?: boolean,

    /** Callback to render the item for this field */
    renderItemFn?: EditableTableRenderItemFn<T>,

    /** Optional, will pass to exportProps when exporting */
    type?: 'number' | 'string' | 'date' | 'money' | 'boolean' | 'other',

    /** Minimum width at which to show this field */
    showAtWidth?: number,

    /** Use a subgrid for this column */
    useSingleLine?: boolean,

    /** Width, as a number (of pixels) or a string, e.g. '1em' */
    width?: string | number
}

export type EditableTableSortEntry<T> = {
    /** This must match a label in properties */
    label: string,

    /** What kind of object are we sorting? */
    type?: 'string' | 'number' | 'date' | 'money' | 'boolean' | 'other',

    /** Should we ignore case for strings? Defaults to false. */
    ignoreCase?: boolean,

    /** Ascending or descending sort, defaults to asc */
    order?: 'asc' | 'desc' | '',

    /** Custom function for sorting, used if column has a renderItemFn */
    sortFn?: (a: T, b: T) => number,

    /** Signature must be (a, b, sortProp), where sortProp is this entry in sortProps */
    useFormatted?: boolean /** Just parse out what is in the field?  Still uses the type, but doesn't ask */
}

export type EditableTableSumEntry<T> = {
    /** This must match a label in properties */
    label: string,

    /** Is this a regular number, or money? */
    type: 'number' | 'money',

    /** Do not format money? */
    textOnly?: boolean,

    /** Do not include cents for money formats? */
    noCents?: boolean,

    /** Custom rendering function, is not accessible just by the label as a field */
    renderItemFn?: EditableTableRenderItemFn<T[]>,
}

export type EditableTableOtherAction<T> = {
    /** A function to call - passes the item back */
    action: (item: T | T[], callback?: VoidBoolCallback) => void,

    /** Color for the icon/text */
    color?: string,

    /** Should the label be hidden? */
    hideLabel?: boolean,

    /** How to justify the content */
    justify?: JustifyType,

    /** A label for the UI */
    label: string,

    /** Label to use for the display, if different from label */
    displayLabel?: string | ReactNode,

    /** If true, only show if multiple items are selected */
    multiOnly?: boolean,

    /** Icons.ICON_CLASS to use when the button is selected */
    selectedIcon?: IconType,

    /** Alternatively, use a function to determine the icon */
    selectedIconFn?: (item: T) => IconType,

    /** Optional text to show with the icon */
    selectedText?: string,

    /** Only show this column if the width of the container is at least n pixels */
    showAtWidth?: number,
    showIf?: (items: T | T[]) => boolean,

    /** Icons.ICON_CLASS to use when the button is not selected */
    unselectedIcon?: IconType,

    /** Width CSS param */
    width?: string
}

export type OnBeforeAddFn = (param?: string) => void;
export type WithItemFn<T> = (item?: T) => void;
// Note that in some rare cases we will return something other than type T, e.g. it might have multiple type or be an object with the T embedded with other properties.
export type OnAddFn<T, V = T> = (item: V | V[], cb?: VoidBoolCallback) => void | boolean | Promise<void | boolean>;
export type OnDeleteFn<T> = (item: T, cb?: VoidCallback) => void;
export type OnDeleteManyFn<T> = (items: T[], cb?: VoidCallback) => void;
export type OnEditFn<T, V = T> = (item: V | V[], cb?: VoidBoolCallback) => void | boolean | Promise<void | boolean>;
export type OnDropFn<T> = (e: DragEvent, source: T, target: T, moveBackward: boolean) => void;

export type EditableTableProps<T, V = T> = {
    /** Forwarded ref to the table */
    ref?: React.Ref<EditableTableHandle>,

    /** Is the onAdd method async? */
    asyncAdd?: boolean,

    /** Is the edit function async? */
    asyncEdit?: boolean,

    /** Can new items be added? */
    canAdd?: boolean,

    /** Callback function to determine if an item can be deleted */
    canDeleteFn?: (item: T) => boolean,

    /** Callback to get the "can't delete" message for a given element */
    canDeleteMessageFn?: (item: T) => ReactNode,

    /** Callback function to determine if a group of items can be deleted */
    canDeleteManyFn?: (items: T[]) => boolean,

    /** Message to show if multiple items can not be safely deleted */
    canDeleteMessage?: ReactNode,

    /** Callback function to determine if an item can be edited */
    canEditFn?: (item: T) => boolean,

    /** If available, callback to get styles to apply to each row. Use regular javascript-CSS like backgroundColor. */
    rowStyleFn?: (item: T, columnIndex?: number, totalColumns?: number) => CSSProperties,

    /** Custom labels for types that we can add, if multiple object types are supported */
    customAddLabels?: EditableTableCustomAddLabel[],

    /** A React component to use for adding/editing */
    editor?: Function | any,

    /** The id of the parent entity, if any */
    entityId?: string,

    /** The name of the entity type being shown */
    entityName: string,

    /** Display name for the entityName */
    displayEntityName?: string,

    /** If an external drop is being applied, this is the event object, e */
    dropEvent?: any,

    /** Environment variables to send to Editor */
    env?: any,

    /** A filter component, complete with its own callbacks */
    filterComponent?: ReactElement | null,

    /** Use a flow style, where the table acts like a normal html div, rather than a scrolling widget */
    flowStyle?: boolean,

    /** Should the list be hidden when the Editor is open? */
    hideGridOnEdit?: boolean,

    /** Should we hide the 'actions' column? */
    hideStandardActions?: boolean,

    /** Should headers be hidden if there are now rows? */
    hideHeadersIfNoData?: boolean,

    /** Don't ask, just delete */
    hideDeleteWarnings?: boolean,

    /** If the id field is not "id", what is it? */
    idField?: string,

    /** If there is no id field on each item, what shall we use as an id? */
    idFn?: (item: T) => string | number,

    /** Can rows in this table be dragged? */
    isDraggable?: boolean,

    /** List of items to deal with */
    items: T[],

    /** Show loading spinner (for when data is being fetched) */
    loading?: boolean,

    /** Override the width in pixels where we consider something in mobile mode */
    mobileWidth?: number,

    /** Can items in this list be multi-selected with a checkmark to the left?
     * This enables multi-select-only buttons, like merge. Delete is always multi-selectable. */
    multiSelect?: boolean,

    /** a unique name for the table, used to cache state */
    name: string,

    /** Paging size (if paged) */
    pageSize?: number,

    /** Filters */
    toggles?: ToggleProp[],

    /** If we are creating groupings, put that info here */
    groups?: EditableTableGroupEntry[],

    /** Should we ignore toggles? Used when parent is searching. */
    ignoreToggles?: boolean,

    /** Should the toggles be indented left (for when hiding charts)? */
    indentToggles?: boolean,

    /** Properties to display (in order) */
    properties: EditableTablePropertyEntry<T>[],

    /** Custom confirm delete function. */
    confirmDeleteWarningFn?: (item: T) => { canDelete: boolean, message: ReactNode },

    /** Props to pass through to the editor */
    editorProps?: any,

    /** Is this in an embedded dialog box? Will use native ok/cancel dialogs */
    embedded?: boolean,

    /** Props to handle custom export calls */
    exportProps?: EditableTableExportEntry<T>[],

    /** If provided, use this name for file exports */
    exportTitle?: string,

    /** Rotate column labels */
    rotateLabels?: boolean,

    /** Rotation angle in degrees (default: 70) */
    rotationAngle?: number,

    /** Border length for rotated labels in em units (used with rotateLabels) */
    rotationLength?: number,

    /** Props to allow sorting (asc and desc) */
    sortProps?: EditableTableSortEntry<T>[],

    /** Props to allow summing (summing up a field) */
    sumProps?: EditableTableSumEntry<T>[],

    /** Props for filtering */
    filterPropChoices?: EditableTableFilterChoices[],

    /** A "unique key" field to display on delete. NOT a UUID.
     * Must have one of the two!
     * TODO: For uniqueKey, take a string or a function, then do the right thing. */
    uniqueKey?: string,
    uniqueKeyFn?: (item: T) => string,

    /** CRUD callbacks */

    /** Called before adding an item */
    onBeforeAdd?: OnBeforeAddFn,

    /** Called after adding an item
     * TODO: Are we really passing V, which could be multiple things, or just T?
     *       Are there any uses that care, or can we just made this a VoidCallback?
     */
    onAfterAdd?: VoidCallback,

    /** Called when an item is ready to add */
    onAdd?: OnAddFn<V>,

    /** Called before an add/edit */
    onBeginEdit?: WithItemFn<T>,

    /** Called after an add/edit (or cancel) */
    onEndEdit?: VoidCallback,

    /** Called when a deletion is requested */
    onDelete?: OnDeleteFn<T>,

    /** Called when more than one deletion is requested */
    onDeleteMany?: OnDeleteManyFn<T>,

    /** Called after a deletion */
    onAfterDelete?: WithItemFn<T>,

    /** Called when an edit is requested */
    onEdit?: OnEditFn<V>,

    /** Drag and Drop callbacks */

    /** (e, source) */
    onDrag?: (e: DragEvent, source: T) => void,

    /** (e, source, target) */
    onDragEnd?: (e: DragEvent, source: T, target: T) => void,

    /** (e, source, target) */
    onDragOver?: (e: DragEvent, source: T, target: T) => void,

    /** (e, source) */
    onDragStart?: (e: DragEvent, source: T) => void,

    /** (e, source, target) */
    onDragEnter?: (e: DragEvent, source: T, target: T) => void,

    /** (e, source, target) */
    onDragExit?: (e: DragEvent, source: T, target: T) => void,

    /** (e, source, target) */
    onDragLeave?: (e: DragEvent, source: T, target: T) => void,

    /** (e, source, target) */
    onDrop?: OnDropFn<T>,

    /** Actions (name, callback), e.g. { name: 'Add', action: addName }
     * Will always pass back the item that is being acted on as the only parameter to the action */
    otherActions?: EditableTableOtherAction<T>[],

    /** If there are some presets that you want to use for new objects, set them here and they will be merged in */
    presets?: any,

    /** Should the read-only eye icon be shown? */
    showReadOnlyIcon?: boolean,

    /** At what point do we consider the display "small" for displayLabelSmall purposes? */
    smallWidth?: number,

    /** If we want to start off with a selected item, we will need a reference to it, and optionally its add label (param) */
    startWith?: T,
    startWithParam?: string,

    /** Like startWith, but accepts an ID string and finds the item internally via getId() */
    startWithId?: string,

    /** Start in the add screen */
    startWithNew?: boolean,

    /** When set, navigate to this path (e.g., 'home') when editing ends via OK or Cancel */
    returnTo?: string,

    /** Updates browser URL to `{urlPrefix}?item={id}` when an item is opened (for bookmarking).
     *  Also extracts entity ID from URL on mount if no startWithId is provided. */
    urlPrefix?: string,

    /** For nested tables inside editor cards: adds/removes `&{urlQueryKey}={id}` to the
     *  current URL's query params when an item is opened/closed. Preserves other params. */
    urlQueryKey?: string,

    /** Opt-in cache-bust for column-width measurement. When this string
     *  changes, the persisted width cache is bypassed and previously
     *  growth-locked widths are reset so the table re-measures against
     *  the new row set. Use it when a *filter* changes the data shape
     *  enough that prior widths are no longer appropriate (e.g. the
     *  Trash page's per-entity-type filter). Sort changes should NOT
     *  set this — sort doesn't change the column-content distribution. */
    measurementKey?: string,
};

type Filter = ColumnFilter;

/** The max allowable rows (CSS grid limits to 1000 like it or not). */
// const MAX_ALLOWABLE_ROWS = 50;

let seed = Math.random();

export type CustomActionButtonOnClick<T> = (oa: EditableTableOtherAction<T>, item: T) => void;

export type CustomActionButtonProps<T> = {
    entityName: string,
    icon: IconType,
    item: T,
    oa: EditableTableOtherAction<T>,
    hideLabel?: boolean,
    onClick: CustomActionButtonOnClick<T>,
}

function CustomActionButton<T>(props: CustomActionButtonProps<T>) {
    const {oa, icon, item, entityName, hideLabel} = props;

    const onClick = useCallback(() => {
        props.onClick(oa, item);
    }, [item, oa, props]);

    const title = oa.selectedText || oa.displayLabel || oa.label;
    const label = hideLabel ? '' : title;

    return <IconButton key={oa.label}
                       id={`CAB-${entityName}-${oa.label}-${seed++}-View`}
                       title={title}
                       label={label}
                       color={oa.color || 'black'}
                       textColor={oa.color || 'black'}
                       type={icon}
                       onClick={onClick}
    />;
}

export type CustomButtonProps = {
    label?: string,
    icon: IconType,
    data?: any,
    additionalArgs?: any[],
    color?: string,
    id?: string,
    disabled?: boolean,
    size?: IconSizes,
    title?: ReactNode,
    onClick?: (e: MouseEvent, data?: any, additionalArgs?: any[]) => void,
    useDelay?: boolean
}

const CustomButton: FC<CustomButtonProps> = props => {
    const altProps = useMemo(() => {
        return {
            ...{
                additionalArgs: [],
                size: Icons.IconSizeNormal,
            },
            ...props,
        };
    }, [props]);

    const {label, icon, id, color, size, data, title, additionalArgs = [], useDelay, disabled} = altProps;

    const onClick = useCallback((e: MouseEvent) => {
        altProps.onClick?.(e, data, ...additionalArgs);
    }, [additionalArgs, data, altProps]);

    return <IconButton id={id || `CB_${label || title}`}
                       key={`CB_${label || title}`}
                       type={icon}
                       color={color}
                       textColor={'black'}
                       label={label}
                       size={size}
                       onClick={onClick}
                       title={title}
                       useDelay={useDelay}
                       disabled={disabled}
                       tabIndex={-1}
    />;
}

/**
 * Editable table is a flexible React (and CSS) Grid, capable of adding, deleting and editing objects with zero
 * UI from the user.
 *
 * To start, the user must pass an array of objects ("items") to the grid, along with a "uniqueKey" that is used to determine
 * if duplicates are added.  The uniqueKey is a property on the object that can be compared between items.  It is also
 * used in message dialogs for delete confirmation, so don't use an integer, use a string field that means something to
 * the user.  If the objects do not have a natural candidate, then use a wrapper object with the fields needed.
 *
 * Custom actions are supported, as well as functions to determine when they apply.  The table maintains its state, but
 * it sends callbacks out for every action.
 *
 * To make adding and editing of complex objects possible, an editor component can be passed in.  The item will be passed
 * to the editor on add and edit, with a null or the item.  In addition, there is an env parameter that can be stuffed
 * with whatever context you want so that the editor component does not need to access global scope.
 *
 * The only really complicated bit is the message passing between the calling UI, EditableTable and the Editor.
 * It works like this:
 *
 * 1. Caller passes items and callbacks for all functions to the EditableGrid.
 * 2. User presses on an action, like Add.
 * 3. An Editor is instantiated with the associated item and callbacks for onOk and onCancel.
 * 4. User fills in the Editor's fields, then clicks on OK.
 * 5. Editor calls the onOk callback with the new or updated item, plus other args (you can put as many args in as you want).
 * 6. EditableTable receives the args and calls the onAdd handler passed to it by the Caller. It includes the arguments
 *    returned from the Editor, plus a callback function to be invoked when finished doing whatever the Caller does.
 * 7. EditableTable calls the Caller with all the args and the callback, and puts up a spinner.
 * 8. The Caller does some saves and calls the callback function with no arguments.
 *
 *
 * The User interface has two pieces:  an Grid to display of the elements, and an Editor for adding/editing them.
 * The editor is normally hidden, but when an item is edited or a new item is added, it is displayed up top.
 * In order to display any type of editor, there are a few constraints:
 *
 * 1. The Editor class should assume that it will be given full width of the screen.  Since there will be an Editor
 *    class above it in the DOM, it can implement a .Editor {} CSS class to handle
 * 2. The editor must follow normal guidelines for using the ok/cancel buttons (circular, red/green, X or Checkmark)
 *
 * -- New fun feature: startWithNew! --
 * You can now pass through props.startWithNew, which is similar to startWith (which opens a particular item),
 * but startWithNew obviously opens the new item window.  If this is a multi-type table, the param prop will
 * be used to determine what kind of new item to create.  This is used by DocumentBlock's EditableTable to
 * enable drag-and-drop pass-through!  See notes in DocumentBlock.onDrop for more details, and to see how to implement
 * drag-and-drop passthrough using startWithNew, param, and env props.
 *
 * -- New fun feature: multiSelect! --
 * If multiSelect is true, checkboxes will show up on the left of the table.  When more than one item is selected (checked)
 * system buttons (delete: yes, edit: no, add: no) and buttons found in otherActions will be shown if multiSelect=true.
 * This enables a user to work on multiple entries at a time.  The usual case for this is delete, but merge is another good one.
 * Export would also be a good use case.
 */

export type EditableTableItem<T> = {
    id?: string,
    entityId?: string,
    clone?: () => T
}

/**
 * Exported function for EditableTable.
 */
export type EditableTableHandle = {
    /**
     * Reset the paging index to 0
     */
    resetPaging: () => void,

    /**
     * Programmatically open the inline editor for a new row, with the given param
     * passed through to the editor. Equivalent to the user clicking the toolbar
     * Add button (or one of the customAddLabels) — same state transitions, same
     * lifecycle callbacks. Use this from siblings of the table (e.g. a help
     * dialog) that want to trigger the standard add flow without forking it.
     */
    beginAdd: (param?: string) => void,
}

type EditableTableState<T> = {
    clientHeight: number;
    current: T | null;
    /** Persisted multi-column sort stack (v1 shape). Old cached `currentSortLabel` is silently dropped. */
    sortStack_v1: SortStackEntry[];
    /** Persisted filters (v2 shape: ColumnFilter[]). Old cached `filters` is silently dropped. */
    filters_v2: Filter[];
    gridScrollTop: number;
    hasAdded: boolean;
    height: number;
    isAdding: boolean;
    isEditing: boolean;
    mobile: boolean;
    newId: string | null;
    pageNumber: number;
    param: string;
    scrollHeight: number;
    scrollTop: number;
    scrollWidth: number;
    selectedRows: string[];
    showFilter: boolean;
    sortProps: EditableTableSortEntry<T>[];
    startWithNew: boolean;
    toggles: ToggleProp[],
    viewOnly: boolean;
    width: number;
};

const EditableTable = forwardRef(function EditableTable<T extends EditableTableItem<T>, V>(
    props: EditableTableProps<T, V>,
    ref: React.ForwardedRef<EditableTableHandle>
) {
    const {
        confirmDeleteWarningFn,
        entityId,
        entityName,
        displayEntityName,
        embedded,
        flowStyle,
        hideDeleteWarnings,
        hideGridOnEdit,
        hideHeadersIfNoData,
        hideStandardActions,
        idFn,
        idField,
        ignoreToggles,
        isDraggable,
        indentToggles,
        loading,
        mobileWidth,
        multiSelect,
        onAdd,
        onAfterAdd,
        onAfterDelete,
        onBeforeAdd,
        onBeginEdit,
        onDelete,
        onDeleteMany,
        onDrag,
        onDragStart,
        onDragEnd,
        onDragEnter,
        onDragExit,
        onDragOver,
        onDragLeave,
        onDrop,
        onEdit,
        onEndEdit,
        asyncAdd,
        canDeleteFn,
        canDeleteManyFn,
        canDeleteMessage,
        canDeleteMessageFn,
        canEditFn,
        rotateLabels,
        rotationAngle = 70,
        rotationLength,
        rowStyleFn,
        showReadOnlyIcon,
        smallWidth,
        startWith,
        startWithId,
        startWithParam,
        returnTo,
        urlPrefix,
        urlQueryKey,
        uniqueKey,
        uniqueKeyFn
    } = props;
    const propsName = props.name;
    const propItems = props.items;

    console.assert(!!propsName, 'EditableTable requires a name prop');
    const cacheKey = `editable-table-${propsName}`;
    const {cacheValue, cacheReset, cacheSave} = useComponentCache<EditableTableState<T>>(cacheKey);

    const canAdd = props.canAdd !== undefined ? props.canAdd : true;
    // const pageSize = (props.pageSize ?? 0) >= 0 ? props.pageSize : MAX_ALLOWABLE_ROWS;

    // Are we currently adding an item?  This will be false until we click on an Add button or startWithNew is true
    const [isAdding, setIsAdding] = useState<boolean>(cacheValue('isAdding', props.startWithNew ?? false));

    // This is the currently selected item. We currently only allow one item to be open at a time.
    // If startWith is not null, this will be the object to start with.
    // Restore from cache, with startWith prop as fallback
    // A cached row is a snapshot from when the table last unmounted. Re-resolve it from the
    // live items by id, so the editor reopens on the current row: the editor reads its
    // `current` prop only at mount, and the prop-sync effect below runs after that (OWA-526).
    const [initialCurrent] = useState<T | null>(() => {
        const restored = cacheValue('current', startWith ?? null);
        if (!restored || restored === startWith) return restored;
        const rowId = (item: T) => `${idFn ? idFn(item) : idField ? item[idField as keyof T] : item.id}`;
        // Always the live row when it exists: the open row is not a draft (the editor caches
        // its own draft), and the prop-sync effect below also follows the live row.
        return propItems?.find(item => rowId(item) === rowId(restored)) ?? restored;
    });
    const [current, setCurrent] = useStateWithCallback<T | null>(initialCurrent);

    // Are we currently editing an item?
    // Derive from startWith/current - don't cache independently to avoid sync issues
    const [isEditing, setIsEditing] = useState<boolean>(!!startWith || !!initialCurrent);

    // Are we currently deleting an item? In flight: never cached, or a remount restores it and stays disabled (OWA-1355)
    const [isDeleting, setIsDeleting] = useStateWithCallback<boolean>(false);

    // Are we in view-only mode?  This is really just a flag we pass through to indicate to the editor
    // that we do not want to allow editing.  Not really used anymore.
    const [viewOnly, setViewOnly] = useState<boolean>(cacheValue('viewOnly', false));

    // Used to determine if an item was just added, and if so focus on the Add button again.
    const [hasAdded, setHasAdded] = useState<boolean>(cacheValue('hasAdded', false));

    // If we are adding a new item, we generate a new uuid and pass it through.  That way we can keep track
    // of it without some messy callback system.
    const [newId, setNewId] = useState<string | null>(cacheValue('newId', null));

    // Are we using mobile mode?  This is calculated in onResize(), which is called any time the display
    // is resized.
    const [mobile, setMobile] = useState<boolean>(cacheValue('mobile', isMobileView(props.mobileWidth || MOBILE_WIDTH)));

    // The current height of the viewport
    const [height, setHeight] = useState<number>(cacheValue('height', 0));

    // The current width of the viewport
    const [width, setWidth] = useState<number>(cacheValue('width', 0));

    // The table's "host width" — the width of the element that contains
    // EditableTable, as reported by ResizeObserver.  The host's size is
    // determined by the surrounding layout (page region, dialog, card
    // column, sidebar), NOT by the table's own contents — so reading it
    // for `showAtWidth` decisions can't form a feedback loop the way
    // contentRef.clientWidth/scrollWidth can near the mobile boundary.
    //
    // This is what the EditableTable's "viewport" really is: not the
    // browser window, but the container the table has been placed in.
    // A table in a 600px-wide dialog should hide its 900px-threshold
    // columns regardless of how wide the window is.
    const [hostWidth, setHostWidth] = useState<number>(0);

    // The height of the __content box
    const [clientHeight, setClientHeight] = useState<number>(cacheValue('clientHeight', 0));

    // The width of the __content box
    const [scrollWidth, setScrollWidth] = useState<number>(cacheValue('scrollWidth', 0));

    // The height of the __content scrollable area
    const [scrollHeight, setScrollHeight] = useState<number>(cacheValue('scrollHeight', 0));

    // Position of the __content scrollbar
    const [scrollTop, setScrollTop] = useState<number>(cacheValue('scrollTop', 0));

    // Position of the __grid scrollbar. We capture the last two states to check for oscillation
    const gridScrollTop = useRef<number>(cacheValue('gridScrollTop', 0));

    // Store the param passed through from startWithParam, or set in onBeginAdd based on the param prop of
    // a property.  This is used if there are more than one possible types to create, e.g. Documents.
    const [param, setParam] = useState<string>(cacheValue('param', startWithParam || ''));

    // If we are paged, what page are we on?
    // const [pageNumber, setPageNumber] = useStateWithCallback<number>(cacheValue('pageNumber', 0));

    // What filters should be applied?
    // Cache key is 'filters_v2' so old persisted shape ({values, operator?}) is silently dropped.
    const [filters, setFilters] = useState<Filter[]>(cacheValue('filters_v2', []));

    // Live draft from FilterDialog — when the user is editing a filter, the dialog pushes
    // its current state here so the table behind it filters in real-time. Cleared on
    // dialog cancel / apply (apply commits to `filters` instead).
    // Shape: {label: string, filter: ColumnFilter | null}. label identifies which column
    // the draft applies to; filter=null means "no filter for that column right now".
    const [draftFilter, setDraftFilter] = useState<{label: string, filter: ColumnFilter | null} | null>(null);

    // Live draft sort stack from FilterDialog — the entire sort priority list, not just the
    // current column's entry. The dialog renders + reorders the full stack and pushes the
    // result up so the table re-sorts behind it in real-time. null when no dialog is open.
    const [draftSortStack, setDraftSortStack] = useState<SortStackEntry[] | null>(null);

    // Toggles for the grid.  These are passed through as props. The cache restores only each
    // declared toggle's on/off state, so a toggle the page no longer declares (an open tab across
    // a deploy) is dropped rather than drawn beside its replacement.
    const [toggles, setToggles] = useState<ToggleProp[]>(() => {
        const cached = cacheValue('toggles', [] as ToggleProp[]);
        return (props.toggles || []).map(toggle => {
            const saved = cached.find(c => c.label === toggle.label);
            return saved ? {...toggle, enabled: saved.enabled} : toggle;
        });
    });

    // The current sort priority list. First entry = primary sort; subsequent entries break
    // ties for rows that are equal on all higher-priority entries (Excel multi-column semantics).
    // Cache key 'sortStack_v1' so any older single-column shape ('currentSortLabel') is silently dropped.
    const initialSortStack: SortStackEntry[] = (props.sortProps ?? [])
        .filter(sp => sp.order === 'asc' || sp.order === 'desc')
        .map(sp => ({label: sp.label, order: sp.order as 'asc' | 'desc'}));
    const [sortStack, setSortStack] = useState<SortStackEntry[]>(cacheValue('sortStack_v1', initialSortStack));

    // Override of startWithNew
    const [startWithNew, setStartWithNew] = useState<boolean>(cacheValue('startWithNew', props.startWithNew ?? false));

    // Should we show the filter bar?
    const [showFilter, setShowFilter] = useState<boolean>(cacheValue('showFilter', false));

    // If showFilter, this will be the properties for the dialog.
    // The new shape (currentFilter, checklistChoices, matchCount) doesn't survive cache reload —
    // the dialog rebuilds on every open from current state, so no v2 key needed; just don't read cache here.
    const [filterProps, setFilterProps] = useState<FilterProps<T>>({} as FilterProps<T>);

    // Reference to the filter dialog
    const filterDialogRef = useRef<FilterDialogHandle<T>>(null);

    // Defer the items to allow shell to render first
    const deferredItems = useDeferredValue(propItems);

    // A copy of the sortProps. We will use/modify these in the state. Always splice sortFn
    // back in from the current props.sortProps on read (the cache may have been written by an
    // older build that stripped sortFn before Zustand stopped serialising entries).
    const hydrateSortFns = useCallback((entries: EditableTableSortEntry<T>[] | undefined) => {
        if (!entries) return [];
        return entries.map(sp => {
            if (sp.sortFn) return sp;
            const fromProps = props.sortProps?.find(p => p.label === sp.label)?.sortFn;
            return fromProps ? {...sp, sortFn: fromProps} : sp;
        });
    }, [props.sortProps]);

    const [sortProps, setSortProps] = useState<EditableTableSortEntry<T>[]>(
        hydrateSortFns(cacheValue('sortProps', props.sortProps ? [...props.sortProps] : [])),
    );

    // Track the last props.sortProps reference we synced from. Stored as state (not usePrevious)
    // so we can sync during render — prevents a stale-state window where sortProps and
    // props.properties can disagree, which previously crashed getStandardSortFn.
    const [lastSyncedSortProps, setLastSyncedSortProps] = useState(props.sortProps);
    if (props.sortProps !== lastSyncedSortProps) {
        setLastSyncedSortProps(props.sortProps);
        setSortProps(props.sortProps ? [...props.sortProps] : []);
    }

    // This will hold the unique IDs of the selected items.
    const [selectedRows, setSelectedRows] = useState<string[]>(cacheValue('selectedRows', []));

    // Shall we reset the state cache when the component is unmounted?
    const doResetRef = useRef<boolean>(false);

    // --- Parent subEditActive unwind on unmount -----------------------------
    // EditableTable notifies the parent's edit counter via onBeginEdit/
    // onBeforeAdd (open) and onEndEdit/onAfterAdd (close). The button-driven
    // close paths balance, but if the table unmounts mid-edit (ContentBlock
    // viewport/tab teardown, a collapse, an ErrorBoundary swallowing a render
    // error) the close callback never fires and the +1 leaks into the parent
    // counter, stranding the card — its OK/Cancel guard refuses forever with no
    // editor on screen. Track an outstanding open and fire onEndEdit (the
    // cancel-path decrement) on unmount. The clear-effect drops the flag once
    // the editor has closed normally, so we never double-decrement.
    const parentEditOpenRef = useRef<boolean>(false);
    const onEndEditRef = useRef(onEndEdit);
    useEffect(() => {
        onEndEditRef.current = onEndEdit;
    });
    useEffect(() => {
        if (!isAdding && !isEditing && !startWith) parentEditOpenRef.current = false;
    }, [isAdding, isEditing, startWith]);
    useEffect(() => () => {
        if (parentEditOpenRef.current) {
            parentEditOpenRef.current = false;
            onEndEditRef.current?.();
        }
    }, []);

    // For returnTo navigation
    const [, navigateET] = useLocation();

    // Track which startWithId value has been resolved (allows re-triggering with a new ID)
    const lastHandledStartWithId = useRef<string | null>(null);

    // Track whether we've cosmetically updated the URL via replaceState
    const urlUpdatedRef = useRef<boolean>(false);

    // True when the current edit was opened via a deep link (prop-based startWithId or
    // URL-derived ?item=).  False for table row clicks.  doReturnTo only navigates for
    // deep links — table row clicks shouldn't redirect the user on close.
    const isDeepLinkEditRef = useRef<boolean>(false);

    // On mount: extract entity ID from URL query param (?item=... or ?{urlQueryKey}=...)
    const [urlDerivedStartWithId] = useState<string | undefined>(() => {
        if (startWithId) return undefined;
        const params = new URLSearchParams(window.location.search);
        if (urlQueryKey) return params.get(urlQueryKey) || undefined;
        if (urlPrefix) return params.get('item') || undefined;
        return undefined;
    });

    // Navigate to returnTo path when editing ends, or navigate to the page root
    // of the current URL.  Only acts when the edit was opened via a deep link
    // (prop-based startWithId or URL-derived ?item=).  Table row clicks set
    // isDeepLinkEditRef to false, so closing those edits leaves the user on the
    // same page.
    //
    // History note: an earlier implementation called window.history.back() when
    // the user had performed in-app navigation.  That was wrong — back() relies
    // on the browser's history stack, which silentReplaceState mutates behind
    // wouter's back, so back() could land the user anywhere (e.g. /home after
    // closing /cases/<id>).  We now always navigate to the page root — the
    // first path segment of the current URL — which matches the URL's parent
    // regardless of how the user got there.
    const doReturnTo = useCallback(() => {
        if (returnTo) {
            navigateET(returnTo.startsWith('/') ? returnTo : `/${returnTo}`);
        } else if (isDeepLinkEditRef.current) {
            // Page root = first path segment, e.g. '/cases/<id>' → '/cases'.
            // For a path that's already a single segment (e.g. '/cases'), keep it.
            const path = window.location.pathname;
            const firstSlash = path.indexOf('/', 1);
            const pageRoot = firstSlash === -1 ? path : path.substring(0, firstSlash);
            navigateET(pageRoot || '/');
        }
    }, [returnTo, navigateET]);

    // Revert URL when editing/adding ends (catches all exit paths: cancel, save, delete, etc.)
    // Uses silentReplaceState (bypasses wouter) to avoid triggering route re-evaluation
    // that can cascade through useSyncExternalStore subscribers and disrupt parent tables.
    useEffect(() => {
        if (!isEditing && !isAdding && urlUpdatedRef.current) {
            if (urlQueryKey) {
                // Nested table: remove our query key, preserve others
                const params = new URLSearchParams(window.location.search);
                params.delete(urlQueryKey);
                const qs = params.toString();
                silentReplaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
            } else if (urlPrefix) {
                silentReplaceState(null, '', urlPrefix);
            }
            urlUpdatedRef.current = false;
            // Reset so the same deep-link ID can re-trigger if navigated to again,
            // but only for prop-based deep links (startWithId) AND only when this
            // exit is actually closing a deep link. Row clicks also set
            // urlUpdatedRef (because they rewrite the URL via silentReplaceState),
            // but they leave isDeepLinkEditRef false; nullifying lastHandled there
            // would let the deep-link effect re-fire on the same commit (when
            // propItems re-references via a parent useMemo whose deps include
            // isEditing) and reopen the editor right after the user clicks Cancel.
            if (startWithId && isDeepLinkEditRef.current) lastHandledStartWithId.current = null;
        }
    }, [isEditing, isAdding, urlPrefix, urlQueryKey, startWithId]);

    /**
     * Ref for the Add button, which gets focus after every Add action.
     */
    const addRef = useRef<HTMLButtonElement>(null);

    /**
     * Ref for the content area, used during resize to get height and widths, and determine mobile or desktop view.
     */
    const contentRef = useRef<HTMLDivElement>(null);

    /**
     * Ref for the grid area, used to keep the scroll position between component remounts
     */
    const gridRef = useRef<HTMLDivElement>(null);

    /**
     * Deterministic column widths from canvas measurement across every row.
     * Hydrated from sessionStorage on mount (so re-navigation is instant),
     * then re-measured after first paint, when the visible column set changes,
     * and when a measured column's content grows (see `contentSignature.ts`).
     * Keyed by property `label`.
     */
    const [measuredWidths, setMeasuredWidths] = useState<Record<string, number> | null>(null);
    // Bumped when a measured column's cells gain content after the last measurement.
    const [remeasureCount, setRemeasureCount] = useState(0);
    // Content of the measured columns as of the last measurement.
    const measuredContentRef = useRef<ContentSignature>({});

    /**
     * Hysteresis memory for `showAtWidth` columns.  A column that's currently
     * shown only hides when width drops `SHOWATWIDTH_HYSTERESIS` below its
     * threshold; a hidden column only shows when width meets the threshold.
     * Without this, viewport widths near a threshold cause the column to
     * toggle on every measurement, which itself triggers layout shifts.
     */
    const SHOWATWIDTH_HYSTERESIS = 24;
    const showAtWidthMemoryRef = useRef<Map<string, boolean>>(new Map());

    const isPropertyShown = useCallback(
        (p: {label: string; showAtWidth?: number}): boolean => {
            if (!p.showAtWidth) return true;
            const w = hostWidth;
            const prev = showAtWidthMemoryRef.current.get(p.label);
            const showAt = p.showAtWidth;
            const hideAt = p.showAtWidth - SHOWATWIDTH_HYSTERESIS;
            const next = prev === true ? w >= hideAt : w >= showAt;
            showAtWidthMemoryRef.current.set(p.label, next);
            return next;
        },
        [hostWidth],
    );

    /**
     * Resize event. Check the height and width and set mobile or desktop view.  This can determine which columns are shown.
     *
     * We now care about the height, so that we can do virtualized row rendering.
     */
    // Refs for stable _onResize — compare measured values to refs instead of state
    // so the callback doesn't depend on (and get recreated by) every state change.
    const clientHeightRef = useRef(clientHeight);
    const scrollWidthRef = useRef(scrollWidth);
    const scrollHeightRef = useRef(scrollHeight);
    const scrollTopRef = useRef(scrollTop);
    const mobileRef = useRef(mobile);
    const heightRef = useRef(height);
    const widthRef = useRef(width);
    const hostWidthRef = useRef(hostWidth);

    const _onResize = useCallback(() => {
        const element = document.getElementById(`editable-table__content:${propsName}`);
        if (element) {
            const _clientHeight = element.clientHeight;
            const _scrollWidth = element.scrollWidth;
            const _scrollHeight = element.scrollHeight;
            const _scrollTop = element.scrollTop;

            if (_clientHeight !== clientHeightRef.current) { clientHeightRef.current = _clientHeight; setClientHeight(_clientHeight); }
            if (_scrollWidth !== scrollWidthRef.current) { scrollWidthRef.current = _scrollWidth; setScrollWidth(_scrollWidth); }
            if (_scrollHeight !== scrollHeightRef.current) { scrollHeightRef.current = _scrollHeight; setScrollHeight(_scrollHeight); }
            if (_scrollTop !== scrollTopRef.current) { scrollTopRef.current = _scrollTop; setScrollTop(_scrollTop); }
        }

        if (contentRef.current) {
            const _mobileWidth = mobileWidth || 800;
            // Use clientWidth (parent-determined, doesn't change with grid content) to avoid
            // feedback loop where column visibility changes scrollWidth across thresholds.
            // Fall back to scrollWidth when clientWidth is too small (e.g. test environments
            // where absolute-positioned elements may not have proper parent dimensions).
            const _clientW = contentRef.current.clientWidth;
            const _scrollW = contentRef.current.scrollWidth;
            const contentWidth = (_clientW >= _mobileWidth) ? _clientW : _scrollW;

            // Mobile-state hysteresis: a small viewport oscillation right at
            // the mobileWidth boundary must not toggle the `hideOnMobile`
            // columns in and out.  Once in mobile mode, stay in mobile until
            // width climbs SHOWATWIDTH_HYSTERESIS above the threshold; once
            // out, stay out until width drops to or below the threshold.
            const wasInMobile = mobileRef.current;
            const mobileThreshold = wasInMobile ? _mobileWidth + SHOWATWIDTH_HYSTERESIS : _mobileWidth;
            const _mobile = isMobileView(_mobileWidth) || contentWidth <= mobileThreshold;
            if (_mobile !== mobileRef.current) { mobileRef.current = _mobile; setMobile(_mobile); }

            const _height = contentRef.current.clientHeight;
            if (_height !== heightRef.current) { heightRef.current = _height; setHeight(_height); }

            const _width = (_clientW >= _mobileWidth) ? _clientW : _scrollW;
            if (_width !== widthRef.current) { widthRef.current = _width; setWidth(_width); }
        }
    }, [propsName, mobileWidth]);

    const onResize = useMemo(() => debounce(_onResize, 200, {immediate: false}), [_onResize]);

    // The grid is absolutely positioned inside __content and must start where the
    // in-flow elements before it (the add bar, the multi-actions bar) end. Those bars
    // wrap on narrow screens, so their height is not a constant; this keeps the
    // --et-grid-top custom property equal to the bottom edge of the grid's previous
    // sibling and re-evaluates it whenever that sibling or the content box resizes.
    // (A fixed 2.7em / 3.2em offset used to stand in for this and put a wrapped filter
    // bar on top of the first rows; putting the grid in flow instead disturbed the
    // column-width measurement, see EditableTable.less.)
    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!content || typeof ResizeObserver === 'undefined') return;
        let frame = 0;
        const apply = () => {
            frame = 0;
            const grid = gridRef.current;
            const prev = grid?.previousElementSibling as HTMLElement | null;
            const top = prev
                        ? prev.offsetTop + prev.offsetHeight + (parseFloat(getComputedStyle(prev).marginBottom) || 0)
                        : 0;
            content.style.setProperty('--et-grid-top', `${Math.round(top)}px`);
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(apply); };
        const observer = new ResizeObserver(schedule);
        observer.observe(content);
        for (const child of content.children) {
            if (child !== gridRef.current) observer.observe(child);
        }
        apply();
        return () => {
            observer.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    });

    // Resize listener — depends only on stable onResize
    useEffect(() => {
        window.addEventListener('resize', onResize);
        // Call _onResize directly (not debounced) so the first measurement is synchronous.
        // The debounced onResize has {immediate: false}, so it would delay 200ms — leaving
        // width === 0 and triggering the loading state.
        _onResize();
        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, [onResize, _onResize]);

    /**
     * Host-width observer.
     *
     * Watches the parent element of `contentRef` and reports its
     * `clientWidth` into `hostWidth` state.  The parent's size is set by
     * the layout that contains EditableTable (page region, dialog, card
     * column, sidebar) — it does NOT change in response to which columns
     * the table happens to render, so it's the right signal for
     * `showAtWidth` visibility decisions.
     *
     * ResizeObserver fires synchronously after the layout that caused the
     * resize, with no debouncing — so the test setup can rely on a small
     * waitFor delay seeing the new state.  Window resizes propagate to
     * the parent and trigger this observer as well.
     */
    // useLayoutEffect, not useEffect: the initial host-width read MUST
    // happen BEFORE the first browser paint, otherwise the first paint
    // renders with `hostWidth = 0`, which evaluates every `showAtWidth`
    // column as hidden and the user sees a minimal-column first frame
    // followed by a snap to the full column set once the observer fires
    // post-paint.  That snap was the user-visible flicker on a normal
    // table mount.
    useLayoutEffect(() => {
        const host = contentRef.current?.parentElement;
        if (!host) return;

        const updateHostWidth = () => {
            const w = host.clientWidth;
            if (w !== hostWidthRef.current) {
                hostWidthRef.current = w;
                setHostWidth(w);
            }
        };

        updateHostWidth();

        const observer = new ResizeObserver(updateHostWidth);
        observer.observe(host);
        return () => observer.disconnect();
    }, []);

    // Scroll restoration — runs once on mount to restore cached scroll positions.
    // Use property assignment instead of scrollTo() — React Compiler wraps
    // useRef in a Proxy, and native Element.scrollTo() throws "Illegal
    // invocation" when `this` is a Proxy instead of a real Element.
    useEffect(() => {
        const cachedScrollTop = scrollTopRef.current;
        if (!!cachedScrollTop && contentRef?.current && contentRef.current.scrollTop !== cachedScrollTop) {
            contentRef.current.scrollTop = cachedScrollTop;
        }
        if (!!gridScrollTop.current && gridRef?.current && gridRef.current.scrollTop !== gridScrollTop.current) {
            gridRef.current.scrollTop = gridScrollTop.current;
        }
    }, []);

    // OWA-1345: the grid is not rendered at all while an item is edited (`<If test={!editing}>` in
    // the render below), so the page gets shorter and the browser clamps whichever scroller holds
    // this table (an ancestor, for a flow table such as an invoice's tasks). Remember the offsets
    // as an editor opens and put them back once the editor has closed and the rows are showing.
    const editScrollRef = useRef<ScrollTarget[]>([]);

    const rememberScrollForEdit = useCallback(() => {
        const targets = captureScroll(contentRef.current);
        const gridTop = gridRef.current?.scrollTop ?? 0;
        if (gridTop > 0) targets.push({resolve: () => gridRef.current, top: gridTop});
        editScrollRef.current = targets;
    }, []);

    const editorOpen = isAdding || isEditing || !!startWith;

    // Restored once, in the commit that mounts the rows again, before paint. A caller that shows
    // `loading` right after the editor closes takes the rows away again after this has run (its
    // prop reaches the table through debounceRender, later than this commit); no caller does.
    useLayoutEffect(() => {
        if (editorOpen) return;
        const targets = editScrollRef.current;
        if (targets.length === 0) return;
        editScrollRef.current = [];
        restoreScroll(targets);
    }, [editorOpen]);

    useEffect(() => {
        return () => {
            // Read at cleanup time: Cancel/OK set the ref after this effect last ran.
            const doReset = doResetRef.current;
            if (doReset) {
                cacheReset();
            }
            else {
                // Save all state on unmount (mirrors what class component did)
                cacheSave({
                    isEditing,
                    isAdding,
                    // Mid-delete, `current` is the row being deleted, not an edit: restoring it
                    // reopens the editor on that row (OWA-1355).
                    current: isDeleting ? null : current,
                    viewOnly,
                    hasAdded,
                    newId,
                    mobile,
                    height,
                    width,
                    clientHeight,
                    scrollWidth,
                    scrollHeight,
                    scrollTop,
                    gridScrollTop: gridScrollTop.current,
                    param,
                    pageNumber: 0, // Paging disabled but required by type
                    filters_v2: filters,
                    toggles,
                    sortStack_v1: sortStack,
                    startWithNew,
                    showFilter,
                    sortProps,
                    selectedRows
                });
            }
        }
    }, [
        cacheReset, cacheSave,
        isEditing, isAdding, isDeleting, current, viewOnly, hasAdded, newId,
        mobile, height, width, clientHeight, scrollWidth, scrollHeight, scrollTop,
        param, filters, toggles, sortStack, startWithNew, showFilter,
        sortProps, selectedRows
    ]);

    useImperativeHandle(ref, () => ({
        // Paging is currently disabled in this component (state hooks commented out);
        // resetPaging is exposed for API stability but is effectively a no-op until paging returns.
        resetPaging() {
            // no-op
        },
        beginAdd(param?: string) {
            try {
                onBeforeAdd?.(param);
                doResetRef.current = false;
                rememberScrollForEdit();
                setIsAdding(true);
                setIsEditing(false);
                setParam(param as string);
                setCurrent(null);
                setNewId(util.genUUID());
            }
            catch (err: any) {
                confirmError('Error', err.message);
            }
        },
    }), [onBeforeAdd, setCurrent, rememberScrollForEdit]);

    /**
     * Get the id for the item, whether an idField, idFn or item.id is used.
     *
     * IMPORTANT: idField / idFn MUST yield a STABLE, UNIQUE string per item. Non-string
     * ids are stringified, so values that stringify identically (e.g. two `null`s ->
     * "null", or numeric `1` and string "1") collapse distinct items to one key. That key
     * drives React `key={itemId}`, `data-row-id`, and `selectedRows`, so collisions cause
     * reconciliation bugs (wrong row highlighted/deleted) and selection cross-talk. The
     * dev-mode effect below surfaces such collisions loudly. (OWA-600)
     *
     * @param {<T>} item
     * @return {string}
     */
    const getId = useCallback((item: T): string => {
        // Try idFn first, then idField, then item.id
        const id = idFn ? idFn(item) : idField ? item[idField as keyof typeof item] : item.id;
        if (typeof (id) !== 'string') return `${id}`;
        else return id;
    }, [idField, idFn]);

    // Dev-only guard: assert getId is unique across the items. A collision is a caller
    // bug (unstable/non-unique idField/idFn) that silently corrupts keys, selection and
    // deletes; warn loudly in development so it is caught before shipping. (OWA-600)
    useEffect(() => {
        if (!import.meta.env.DEV) return;

        const counts = new Map<string, number>();
        for (const item of propItems ?? []) {
            const id = getId(item);
            counts.set(id, (counts.get(id) ?? 0) + 1);
        }
        const duplicates = [...counts.entries()].filter(([, n]) => n > 1).map(([id]) => id);
        if (duplicates.length > 0) {
            console.warn(
                `EditableTable(${entityName}): getId produced duplicate ids ${JSON.stringify(duplicates)}. ` +
                `idField/idFn must yield a stable, unique string per item — colliding ids cause row ` +
                `reconciliation bugs (wrong row highlighted/deleted) and selection cross-talk.`,
            );
        }
    }, [propItems, getId, entityName]);

    // Resolve startWithId (or URL-derived ID): find the item by ID and open it for editing
    const effectiveStartWithId = startWithId || urlDerivedStartWithId;
    const isUrlDerived = !startWithId && !!urlDerivedStartWithId;

    useEffect(() => {
        if (!effectiveStartWithId || lastHandledStartWithId.current === effectiveStartWithId) return;

        const item = propItems.find(item => getId(item) === effectiveStartWithId);
        if (item) {
            lastHandledStartWithId.current = effectiveStartWithId;
            // OWA-1802: a deep link opens the editor only for a row the table lets the user edit.
            if (canEditFn && !canEditFn(item)) return;
            isDeepLinkEditRef.current = true;
            setCurrent(item);
            setIsEditing(true);
            if (isUrlDerived) urlUpdatedRef.current = true;
        }
    }, [effectiveStartWithId, propItems, getId, setCurrent, isUrlDerived, canEditFn]);

    /**
     * Logic to handle changes in props and state.  Because of the sensitivity of changes in this table, we store
     * its state on every change.
     */
    useEffect(() => {
        // If we have been passed a specific item to start with, set those properties
        if (props.startWithNew && !startWithNew) {
            if (isAdding !== props.startWithNew) setIsAdding(props.startWithNew);
            if ((startWithNew as boolean) !== props.startWithNew) setStartWithNew(props.startWithNew);
            if (param !== startWithParam) setParam(startWithParam || '');
        }
    }, [isAdding, param, props.startWithNew, startWithNew, startWithParam]);

    // Check for changes in the current prop (if editing)
    useEffect(() => {
        if ((isEditing || current) && !startWith) {
            // See if the dbAddedOn of the current item has changed
            const newCurrent = current ? propItems.find(c => getId(c) === getId(current)) : null;
            if (current && newCurrent && 'addedOn' in newCurrent && 'addedOn' in current) {
                if (newCurrent.addedOn !== current.addedOn) {
                    setCurrent(newCurrent);
                }
            }
        }
    }, [current, getId, isEditing, propItems, startWith, setCurrent]);

    const doScrollTo = useCallback(() => {
        // pWaitFor(() => !!(gridRef?.current), {interval: 100, timeout: 5000, signal: globalAbortSignal})
        //     .then(() => {
        //         gridRef.current.scrollTo(0, gridScrollTop ?? 0);
        //     })
        //     .catch(_ => {});
    }, []);

    /**
     * Called when an edit is about to start.  Gives the client a chance to take an action before editing starts.
     *
     * @param {SyntheticEvent} e
     * @param {<T>} item - the item that is about to be edited
     * @param {boolean} viewOnly - if this item will be edited in viewOnly mode
     */
    const handleBeginEdit = useCallback((e: SyntheticEvent, item: T, viewOnly: boolean) => {
        e.preventDefault();
        e.stopPropagation();

        // Get a clone of the item (never send the actual item!)
        item = typeof item.clone === 'function' ? item.clone() : {...item};

        if (!onEdit) return;

        try {
            onBeginEdit && onBeginEdit(item);
            parentEditOpenRef.current = true; // outstanding open for unmount-unwind

            doResetRef.current = false;
            isDeepLinkEditRef.current = false;

            rememberScrollForEdit();
            setNewId(null);
            setIsAdding(false);
            setIsEditing(true);
            setViewOnly(viewOnly);
            setCurrent(item);

            if (!startWithId) {
                if (urlQueryKey) {
                    // Nested table: add our query key, preserve others
                    const params = new URLSearchParams(window.location.search);
                    params.set(urlQueryKey, getId(item));
                    silentReplaceState(null, '', `${window.location.pathname}?${params}`);
                    urlUpdatedRef.current = true;
                } else if (urlPrefix) {
                    silentReplaceState(null, '', `${urlPrefix}?item=${getId(item)}`);
                    urlUpdatedRef.current = true;
                }
                // Mark this ID as handled so the deep-link effect doesn't re-open it.
                // Without this, useDeepLinkId in the parent picks up the ?item= param on
                // re-render, passes it back as startWithId, and the deep-link effect treats
                // it as a new deep link — causing a re-open loop after cancel.
                lastHandledStartWithId.current = getId(item);
            }
        }
        catch (err: any) {
            confirmError('Error', err.message);
        }
    }, [onBeginEdit, onEdit, setCurrent, urlPrefix, urlQueryKey, startWithId, getId, rememberScrollForEdit]);

    /**
     * Called when an edit is about to start.  Gives the client a chance to take an action before editing starts.
     *
     * @param {SyntheticEvent} e
     */
    const handleBeginAdd = useCallback((e: SyntheticEvent): void => {
        e.preventDefault();
        e.stopPropagation();

        try {
            onBeforeAdd && onBeforeAdd();
            parentEditOpenRef.current = true; // outstanding open for unmount-unwind

            doResetRef.current = false;

            rememberScrollForEdit();
            setIsAdding(true);
            setIsEditing(false);
            setParam('');
            setCurrent(null);
            setNewId(util.genUUID());
        }
        catch (err: any) {
            confirmError('Error', err.message);
        }

    }, [onBeforeAdd, setCurrent, rememberScrollForEdit]);

    const handleBeginAddLabel = useCallback((e: MouseEvent, param: string, ..._: any[]) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            onBeforeAdd && onBeforeAdd(param);
            parentEditOpenRef.current = true; // outstanding open for unmount-unwind

            doResetRef.current = false;

            rememberScrollForEdit();
            setIsAdding(true);
            setIsEditing(false);
            setParam(param);
            setCurrent(null);
            setNewId(util.genUUID());
        }
        catch (err: any) {
            confirmError('Error', err.message);
        }
    }, [onBeforeAdd, setCurrent, rememberScrollForEdit]);


    const getCurrent = useCallback(() => {
        return startWith || current;
    }, [startWith, current]);

    /**
     * User canceled the add.  Let the caller know as well.
     */
    const handleCancelAdd = useCallback(() => {
        try {
            const _doWork = () => {
                onEndEdit?.();

                doResetRef.current = true;

                setNewId(null);
                setIsAdding(false);
                setIsEditing(false);
                setCurrent(null);
                setStartWithNew(false);
                setParam('');

                doReturnTo();
            };

            // If there was something in the cache, we need to remove it
            if (getCurrent()) {
                window.storageWorker
                    .uncacheItem(
                        getCurrentFirmId(),
                        getCurrentUserId(),
                        entityName.toLowerCase(),
                        'entityId' in getCurrent()! ? entityId : null,
                        getCurrent()!.id || getId(getCurrent()!)
                    )
                    .catch(console.error)
                    .finally(_doWork);
            }
            else _doWork();
        }
        catch (err: any) {
            confirmError('Error', err.message);
        }
        finally {
            doScrollTo();
        }
    }, [getCurrent, onEndEdit, entityName, entityId, setCurrent, getId, doScrollTo, doReturnTo]);

    /**
     * User canceled the edit.  Let the caller know as well.
     */
    const handleCancelEdit = useCallback(() => {
        try {
            const _doWork = () => {
                onEndEdit?.();

                doResetRef.current = true;

                setIsAdding(false);
                setIsEditing(false);
                setCurrent(null);
                setStartWithNew(false);
                setParam('');

                doReturnTo();
            };

            // If there was something in the cache, we need to remove it
            if (getCurrent()) {
                const hasEntityId = Object.prototype.hasOwnProperty.call(getCurrent(), 'entityId');

                if (entityId || hasEntityId)
                    window.storageWorker
                        .uncacheItem(
                            getCurrentFirmId(),
                            getCurrentUserId(),
                            entityName.toLowerCase(),
                            entityId ?? getCurrent()!.entityId,
                            getCurrent()!.id
                        )
                        .catch(console.error)
                        .finally(_doWork);
                else _doWork();
            }
            else _doWork();
        }
        catch (err: any) {
            confirmError('Error', err.message);
        }
        finally {
            doScrollTo();
        }
    }, [getCurrent, entityId, entityName, onEndEdit, setCurrent, doScrollTo, doReturnTo]);

    const addCallback = useCallback((canceled: boolean) => {
        const success = !canceled;

        if (success) {
            setIsAdding(false);
            setIsEditing(false);
            setCurrent(null);
            setHasAdded(true);
            setStartWithNew(false);

            onAfterAdd?.();
            if (addRef.current && ('focus' in addRef.current)) addRef.current.focus();

            doReturnTo();
        }
        else {
            setIsAdding(false);
            setIsEditing(false);
            setIsDeleting(false);
            setHasAdded(false);

            // Balance the +1 from onBeforeAdd. Editors that cancel through
            // their own UI route through handleCancelAdd (which fires
            // onEndEdit), but a caller-side abort — onAdd invoking its
            // callback with cancel=true — lands here instead, and without
            // this the parent's subEditActive counter would stick at +1.
            onAfterAdd?.();
        }
    }, [onAfterAdd, setCurrent, setIsDeleting, doReturnTo]);

    /**
     * This is called from an Editor component.  Ensure that any button click that leads here has called preventDefault
     * and stopPropagation!
     *
     * @param {any} item - this is the item that was added
     */
    const handleAdd = useCallback((item: V) => {
        const onErr = (err: any) => {
            console.error(err);

            setIsAdding(false);
            setIsEditing(false);
            setCurrent(null);

            confirmWarning(`Error adding ${displayEntityName || entityName}`, err.message);
        }

        if (asyncAdd) {
            // For async calls, we need to set the state immediately because we will lose the callback from the onAdd call.
            setIsAdding(false);
            setIsEditing(false);
            setCurrent(null);
            setHasAdded(true);
            setStartWithNew(false);

            // NOTE: Don't fire onAfterAdd or focus from .then() — addCallback
            // does both when invoked with cancel=false (line 1391-1392). Firing
            // here too made onAfterAdd run TWICE on async success, which drove
            // the parent's subEditActive counter negative.
            return (onAdd!(item, (cancel: boolean) => addCallback(cancel)) as Promise<void | boolean>)
                .catch(onErr)
                .finally(() => {
                    doScrollTo();
                });
        }
        else {
            try {
                return onAdd!(item, (cancel: boolean) => addCallback(cancel));
            }
            catch (err: any) {
                onErr(err);
            }
            finally {
                doScrollTo();
            }
        }
    }, [asyncAdd, onAdd, entityName, displayEntityName, setCurrent, addCallback, doScrollTo]);

    /**
     * Handle errors in the onEdit handler. Deduplicates code for sync and async.
     *
     * @param {any} err
     */
    const handleEditError = useCallback((err: any) => {
        console.error(err);

        setIsAdding(false);
        setIsEditing(false);
        setCurrent(null);
        setStartWithNew(false);

        confirmWarning(`Error editing ${displayEntityName || entityName}`, err.message);
    }, [displayEntityName, entityName, setCurrent]);

    /**
     * This is called from an Editor component.  Ensure that any button click that leads here has called preventDefault
     * and stopPropagation!
     *
     * @param {<V>} item - this is the item that was edited, with edits
     */
    const handleEdit = useCallback((item: V) => {
        // Get a clone of the item (never send the actual item!)
        // @ts-ignore
        if (Array.isArray(item)) item = item.map(i => {
            if ('clone' in i && typeof i.clone === 'function') return i.clone() as V;
            else return {...i} as V;
        })
        else {
            // @ts-ignore
            item = typeof item.clone === 'function' ? item.clone() as V : {...item} as V;
        }

        let completedByCallback = false;
        try {
            const result = onEdit!(
                item as unknown as V,
                (cancel: boolean) => {
                    if (!cancel) {
                        completedByCallback = true;
                        setIsAdding(false);
                        setIsEditing(false);

                        onEndEdit?.();
                        doReturnTo();
                    }
                });

            if (result instanceof Promise) {
                return result.then(accepted => {
                    if (accepted === false) return false;
                    setIsAdding(false);
                    setIsEditing(false);
                    if (!completedByCallback) onEndEdit?.();
                    return accepted;
                }).catch(err => {
                    handleEditError(err);
                    onEndEdit?.();
                    return false;
                });
            }
        }
        catch (err: any) {
            handleEditError(err);
        }
        finally {
            doScrollTo();
        }
    }, [onEdit, onEndEdit, handleEditError, doScrollTo, doReturnTo]);

    /**
     *
     * @param {EditableTableSortEntry<{T}>} sortProp
     * @return {Function}
     */
    const getStandardSortFn = useCallback((sortProp: EditableTableSortEntry<T>): (a: any, b: any) => number => {
        const {type, ignoreCase, order, label, useFormatted} = sortProp;
        const asc = (order === 'asc');
        // Defensive: the property may not exist in props.properties if internal sortProps state
        // is momentarily out of sync with props.properties during a prop transition.
        const renderItemFn = props.properties.find(p => p.label === sortProp.label)?.renderItemFn;
        // When a column declares an exportFn, that's the canonical plain-value source for
        // both filter and sort — it exists precisely because renderItemFn produces UI, not a
        // sortable/filterable value. Prefer it; fall back to the formatted-HTML path; finally
        // fall back to the raw property.
        const exportFn = props.exportProps?.find(ep => ep.label === sortProp.label)?.exportFn;

        const gv = (item: T) => {
            if (exportFn) return exportFn(item, label, false);
            if (!useFormatted || !renderItemFn) return item[label];
            if (!sortTextDiv) sortTextDiv = document.createElement('div');
            sortTextDiv.innerHTML = renderToString(renderItemFn(item, label, false));
            return sortTextDiv.textContent || '';
        };

        const pf = (item: T): number => {
            const val = gv(item);
            // Money values may arrive as raw numbers OR pre-formatted strings
            // ("$1,234.56"). Always return a finite number — null/undefined/""
            // sort to 0 instead of producing NaN under (a - b) subtraction.
            if (val == null || val === '') return 0;
            const stripped = String(val).replace(/[^\d.-]/g, '');
            const n = Number(stripped);
            return Number.isFinite(n) ? n : 0;
        };

        const numericValue = (item: T): number => {
            const val = gv(item);
            if (val == null || val === '') return 0;
            const n = typeof val === 'number' ? val : Number(val);
            return Number.isFinite(n) ? n : 0;
        };

        const dateValue = (item: T): number | null => {
            const val = gv(item);
            if (val == null || val === '') return null;
            if (val instanceof Date) {
                const t = val.getTime();
                return Number.isFinite(t) ? t : null;
            }
            const t = new Date(val as any).getTime();
            return Number.isFinite(t) ? t : null;
        };

        const stringSort = (a: T, b: T) => {
            let gva = gv(a);
            let gvb = gv(b);

            if (Array.isArray(gva)) gva = gva.length > 0 ? gva.sort().join(', ') : '';
            if (Array.isArray(gvb)) gvb = gvb.length > 0 ? gvb.sort().join(', ') : '';

            if (gva && !gvb) return asc ? 1 : -1;
            else if (!gva && gvb) return asc ? -1 : 1;
            else if (!gva && !gvb) return 0;
            else if (ignoreCase) return asc ? gva.toLowerCase().localeCompare(gvb.toLowerCase()) : gvb.toLowerCase().localeCompare(gva.toLowerCase());
            else return asc ? gva.localeCompare(gvb) : gvb.localeCompare(gva);
        };

        switch (type) {
            case 'money':
                return (a, b) => asc ? pf(a) - pf(b) : pf(b) - pf(a);
            case 'number':
                return (a, b) => {
                    // numericValue() coerces null/undefined/"" → 0 so
                    // (a - b) is never NaN (NaN is treated as "equal" by
                    // Array.prototype.sort, producing non-deterministic
                    // ordering across browsers).
                    const va = numericValue(a);
                    const vb = numericValue(b);
                    return asc ? va - vb : vb - va;
                };
            case 'date':
                return (a, b) => {
                    // dateValue() returns null for null/undefined/""/Invalid
                    // Date so we always compare finite timestamps. Strings
                    // (cached state hydrated as strings, hand-built mocks)
                    // are coerced via new Date().
                    const va = dateValue(a);
                    const vb = dateValue(b);
                    if (va !== null && vb === null) return asc ? 1 : -1;
                    if (va === null && vb !== null) return asc ? -1 : 1;
                    if (va === null && vb === null) return 0;
                    return asc ? (va! - vb!) : (vb! - va!);
                };
            case 'string':
                return stringSort;
            case 'boolean':
                return (a, b) => {
                    // Extract the field value via gv() like every other type does;
                    // the previous version compared the raw item objects and always
                    // returned 0, making boolean sort a silent no-op.
                    const va = gv(a);
                    const vb = gv(b);
                    if (va === vb) return 0;
                    // false < true in ascending order
                    if (asc) return va ? 1 : -1;
                    return va ? -1 : 1;
                };
            default:
                // console.warn(`EditableTable: ${propsName}.${sortProp.label}: Unknown type: ${type}`);
                return stringSort;
        }
    }, [props.properties, props.exportProps]);

    /**
     * Ensure that the item meets any and all filter criteria.
     *
     * @param {any} item
     * @returns {boolean}
     * @private
     */
    // Effective filter set = persisted filters with the dialog's draft spliced in (if any).
    // The draft replaces any persisted filter on the same column so the user sees the live
    // result of their edits, not the live result on top of the previously-applied filter.
    const effectiveFilters = useMemo<Filter[]>(() => {
        if (!draftFilter) return filters;
        const others = filters.filter(f => f.label !== draftFilter.label);
        return draftFilter.filter ? [...others, draftFilter.filter] : others;
    }, [filters, draftFilter]);

    // Effective sort priority — the committed sortStack, or the dialog's draft if a dialog
    // session is in progress. Source of truth for both rendering (column header icons) and
    // sorting (getSortedItemsOverride's chained comparator).
    const effectiveSortStack = useMemo<SortStackEntry[]>(() => {
        return draftSortStack ?? sortStack;
    }, [draftSortStack, sortStack]);

    // sortProps with the order from the effective stack spliced in, so getStandardSortFn
    // and column-header icon code can keep reading `sortProp.order` without knowing about the
    // stack indirection. Columns not in the stack carry order=''.
    const effectiveSortProps = useMemo<EditableTableSortEntry<T>[]>(() => {
        return sortProps.map(sp => {
            const entry = effectiveSortStack.find(e => e.label === sp.label);
            return entry ? {...sp, order: entry.order} : {...sp, order: ''};
        });
    }, [sortProps, effectiveSortStack]);

    const criteriaMet = useCallback((item: T): boolean => {
        // Cross-column AND: every filter must pass.
        return effectiveFilters.every(filter => {
            const sortProp = sortProps.find(sp => sp.label === filter.label);
            const prop = props.properties.find(p => p.label === filter.label);
            if (!prop) return true;
            const exportProp = props.exportProps?.find(ep => ep.label === filter.label);
            const exportFn: ExportFn<T> | undefined = (exportProp?.exportFn || prop.renderItemFn) as ExportFn<T> | undefined;
            return evaluateColumnFilter(item, filter, prop, sortProp, exportFn);
        });
    }, [effectiveFilters, sortProps, props.properties, props.exportProps]);


    /**
     * Single source of truth for the table's data pipeline: toggles → column filters →
     * multi-column sort → rank filters (top_n / bottom_n). Used by both the deferred-render
     * path (`getSortedItems` / `getSortedItemsOverride`) and the live `sortedItems` memo so
     * the two stay in lockstep.
     */
    const applyFilterAndSortPipeline = useCallback((source: T[]): T[] => {
        let items = source ?? [];

        const isSorted = effectiveSortStack.length > 0;
        const hasToggles = !ignoreToggles && toggles?.length > 0;
        const isFiltered = effectiveFilters?.length > 0;
        if (!isSorted && !hasToggles && !isFiltered) return items;

        if (hasToggles) {
            items = items.filter(item => toggles.every(toggle => toggle.toggleFilterFn(item, toggle.enabled)));
        }

        if (isFiltered) {
            items = items.filter(criteriaMet);
        }

        // Multi-column sort: chain comparators from the sortStack. First entry is primary;
        // ties fall through to the next entry. Matches Excel semantics.
        if (isSorted) {
            const comparators = effectiveSortStack
                .map(entry => {
                    const sp = effectiveSortProps.find(p => p.label === entry.label);
                    if (!sp) return null;
                    // Custom sortFn returns ascending order; flip the result for desc.
                    if (sp.sortFn) {
                        const fn = sp.sortFn;
                        return entry.order === 'desc' ? (a: T, b: T) => -fn(a, b) : fn;
                    }
                    return getStandardSortFn(sp);
                })
                .filter((fn): fn is (a: T, b: T) => number => fn !== null);

            if (comparators.length > 0) {
                items = [...items].sort((a, b) => {
                    for (const cmp of comparators) {
                        const r = cmp(a, b);
                        if (r !== 0) return r;
                    }
                    return 0;
                });
            }
        }

        // Rank-based filters run AFTER sort so visible order is preserved while the slice
        // is computed against the column's own value.
        const rankFilters = effectiveFilters.filter(hasRankFilter);
        if (rankFilters.length > 0) {
            const exportFns = new Map<string, ExportFn<T> | undefined>();
            for (const f of rankFilters) {
                const ep = props.exportProps?.find(e => e.label === f.label);
                const p = props.properties.find(pp => pp.label === f.label);
                exportFns.set(f.label, (ep?.exportFn || p?.renderItemFn) as ExportFn<T> | undefined);
            }
            items = applyRankFilters(items, rankFilters, props.properties, effectiveSortProps, exportFns);
        }
        return items;
    }, [ignoreToggles, toggles, effectiveSortProps, effectiveSortStack, getStandardSortFn, criteriaMet, effectiveFilters, props.properties, props.exportProps]);

    /**
     * Get the items, sorted and filtered. Accepts an optional override (used to drive sort
     * against the deferred-value items rather than the latest `propItems`).
     *
     * @param {<T>[]} [overrideItems] - override the items to use
     */
    const getSortedItemsOverride = useCallback((overrideItems: T[]): T[] => {
        return applyFilterAndSortPipeline(overrideItems ?? propItems ?? []);
    }, [applyFilterAndSortPipeline, propItems]);

    // Delayed fetch of items
    const getSortedItems = useCallback((): T[] => {
        return getSortedItemsOverride(deferredItems);
    }, [getSortedItemsOverride, deferredItems]);

    /**
     * Get the ids of the selected rows. The ids are usually UUIDs, but can be any kind of string.
     *
     * When search criteria change, we end up with invalid entries in selectedRows.
     */
    const getSelectedRows = useCallback((propItems: T[], currentSelectedRows: string[]) => {
        const itemIds = getSortedItemsOverride(propItems).map(item => getId(item));
        return currentSelectedRows?.filter(id => itemIds.includes(id)) || [];
    }, [getSortedItemsOverride, getId]);

    /**
     * This is called when a delete is requested.  Always asks for permission first.
     *
     * Now supports multi-item delete by calling delete on each of the selected items (but one verification).
     *
     * @param {SyntheticEvent} e
     * @param {any} item - item to be deleted
     */
    const handleDelete = useCallback((e: SyntheticEvent, item: T) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            item = typeof item.clone === 'function' ? item.clone() : {...item};
            const name = uniqueKeyFn ? uniqueKeyFn(item) : item[uniqueKey!];
            const _selectedRows = getSelectedRows(propItems, selectedRows);

            const doDelete = () => {
                setIsDeleting(true);
                setCurrent(item, () => {
                    onDelete!(item, (cancel?: boolean) => {
                        if (cancel) {
                            setIsDeleting(false);
                            return;
                        }

                        const itemId = getId(item);

                        setIsEditing(false);
                        setIsDeleting(false);
                        setCurrent(null);
                        setSelectedRows(_selectedRows.filter(id => id !== itemId));

                        onAfterDelete && onAfterDelete(item);
                    });
                });
            };

            // Ask if they are sure!
            let warning: { canDelete: boolean; message: ReactNode } = {canDelete: true, message: null};
            if (confirmDeleteWarningFn) warning = confirmDeleteWarningFn(item);

            if (warning.canDelete) {
                if (hideDeleteWarnings) doDelete();
                else {
                    confirmWarningOkCancelWithOptions({
                        title: 'Confirmation',
                        message: (warning.message as ReactElement) ||
							<span>Are you sure you want to remove <b>{name}</b>?</span>,
                        okLabel: i18n.cs('yes', true),
                        cancelLabel: i18n.cs('no', true),
                        onOk: () => { // OK
                            doDelete();
                        },
                        onCancel: () => { // Cancel
                            setIsDeleting(false);
                            setIsEditing(false);
                            setIsAdding(false);
                            setCurrent(null);
                        },
                        useNative: embedded
                    });
                }
            }
            else {
                confirmWarning('Can Not Delete', warning.message as ReactElement, 'Understood');
            }
        }
        catch (err: any) {
            console.error(err);

            setIsEditing(false);
            setIsDeleting(false);
            setCurrent(null);

            confirmWarning(`Error deleting ${displayEntityName || entityName}`, err.message);
        }
        finally {
            doScrollTo();
        }
    }, [propItems, uniqueKey, embedded, onDelete, entityName, displayEntityName, uniqueKeyFn, confirmDeleteWarningFn, hideDeleteWarnings, onAfterDelete, getSelectedRows, selectedRows, setIsDeleting, setCurrent, getId, doScrollTo]);

    /**
     * Delete multiple selected entries.
     *
     * @param {SyntheticEvent} e
     * @param {any[]} items
     */
    const handleDeleteMany = useCallback((e: MouseEvent, items: any[]) => {
        e.preventDefault();
        e.stopPropagation();

        // Get a clone of the item (never send the actual item!)
        items = items.map(item => {
            // Make sure we don't error out before updating state!
            return typeof item.clone === 'function' ? item.clone() : {...item};
        });

        const ids = items.map(item => getId(item));
        const newSelectedRows = propItems.filter(item => !ids.includes(getId(item))).map(item => getId(item));

        try {
            // OWA-1762: a row that can not be deleted alone can not be deleted as part of a selection.
            // Run the same refusal check as deleting one row, and refuse the whole selection if any row fails.
            if (confirmDeleteWarningFn) {
                const refused = items
                    .map(item => ({item, warning: confirmDeleteWarningFn(item)}))
                    .filter(({warning}) => !warning.canDelete);
                if (refused.length) {
                    confirmWarning('Can Not Delete', <div>
                        <p>{refused.length === 1 ? 'One selected item can not be deleted' : `${refused.length} selected items can not be deleted`}, so none were deleted.</p>
                        <ul>
                            {refused.map(({item, warning}, index) => <li key={getId(item) ?? index}>
                                <b>{uniqueKeyFn ? uniqueKeyFn(item) : item[uniqueKey!]}</b>
                                {warning.message as ReactElement}
                            </li>)}
                        </ul>
                    </div>, 'Understood');
                    return;
                }
            }

            // Ask if they are sure!
            confirmWarningOkCancelWithOptions({
                title: 'Confirmation',
                message: `Are you sure you want to remove ${items.length} items?`,
                okLabel: i18n.cs('yes', true),
                cancelLabel: i18n.cs('no', true),
                onOk: () => { // OK
                    setIsDeleting(true, () => {
                        // If there is a function to deleteMany, use it, otherwise just call onDelete for each item
                        if (onDeleteMany) onDeleteMany(items, () => {
                            setIsEditing(false);
                            setIsDeleting(false);
                            setSelectedRows(newSelectedRows);
                        });
                        else {
                            // Need to wait until the callback is fired before moving to the next item!
                            let index = 0;

                            // This is the callback from onDelete(). The bounds check gates BOTH the
                            // item read and the recursive call, and `index` advances BEFORE the call.
                            // That keeps the loop correct whether onDelete invokes the callback
                            // synchronously (advancing first avoids re-reading the same item / runaway
                            // recursion) or asynchronously (the terminal callback hits the `else`
                            // branch instead of dereferencing items[items.length] and throwing).
                            const deleteNext = () => {
                                if (index < items.length) {
                                    // OWA-1762: `items` already holds clones (made above), and a clone method lives on
                                    // the prototype, so hand each handler its own clone unchanged.
                                    const item = items[index];
                                    index++;
                                    onDelete!(item, deleteNext);
                                }
                                else {
                                    setIsEditing(false);
                                    setIsDeleting(false);
                                    setSelectedRows(newSelectedRows);
                                }
                            };

                            deleteNext();
                        }
                    });
                },
                onCancel: () => { // Cancel
                    setIsDeleting(false);
                    setIsAdding(false);
                    setIsEditing(false);
                }
            });
        }
        catch (err: any) {
            console.error(err);

            setIsEditing(false);
            setIsDeleting(false);
            setCurrent(null);

            confirmWarning(`Error deleting ${displayEntityName || entityName}`, err.message);
        }
        finally {
            doScrollTo();
        }
    }, [propItems, getId, setIsDeleting, setCurrent, doScrollTo, displayEntityName, entityName, onDelete, onDeleteMany, confirmDeleteWarningFn, uniqueKeyFn, uniqueKey]);

    /**
     * Splits a camel-case or Pascal-case variable name into individual words.
     * @param {string} s
     * @returns {string[]}
     */
    const splitWords = useCallback((s: string): string[] => {
        let re: RegExp = /([A-Za-z]?)([a-z]+)/g;
        let match: RegExpExecArray | null;
        let output: string[] = [];

        /*
         matches example: "oneTwoThree"
         ["one", "o", "ne"]
         ["Two", "T", "wo"]
         ["Three", "T", "hree"]
         */

        match = re.exec(s);
        while (match) {
            output.push([match[1].toUpperCase(), match[2]].join(""));
            match = re.exec(s);
        }

        return output;
    }, []);

    /**
     * Helper function to call the rowStyleFn and handle errors gracefully.
     *
     * @param {any} item
     * @param {number} [columnIndex]
     * @param {number} [totalColumns]
     * @returns {CSSProperties}
     * @private
     */
    const _callRowStyleFn = useCallback((item: T, columnIndex: number, totalColumns: number): CSSProperties => {
        try {
            return rowStyleFn!(item, columnIndex, totalColumns);
        }
        catch (err: any) {
            // Something went wrong, let's return maroon, so we know to check the logs
            console.error(err, {item, rowIndex: columnIndex});
            return {color: 'maroon'};
        }
    }, [rowStyleFn]);

    /**
     * Called when a toggle is clicked.
     *
     * @param {ToggleProp} toggle
     */
    const onClickToggle = useCallback((toggle: ToggleProp) => {
        const _toggles = toggles.map(t => ({...t}));

        // Find the related toggle
        const _toggle = _toggles.find(t => toggle.label === t.label);
        _toggle!.enabled = !toggle.enabled;

        setToggles(_toggles);

        if (toggle.toggleClickFn) toggle.toggleClickFn(_toggle!.enabled);
    }, [toggles, setToggles]);

    /*** Drag and Drop ***/

    /**
     * Helper function, takes an event and returns if the source and target have the same entity types.
     *
     * @param {DragEvent} e
     */
    const isSameType = useCallback((e: DragEvent) => {
        return e.dataTransfer.types.includes(`application/${entityName.toLowerCase()}`);
    }, [entityName]);

    /**
     * Either an Add button/label, or something custom in the case of multiple adders.
     *
     * @param {string} upperEntityName
     */
    const getAddBar = useCallback((upperEntityName: string) => {
        // Don't allow add if we are showing the multiple-item toolbar
        if (getSelectedRows(propItems, selectedRows).length > 0) return null;

        // If there is a filterComponent, we will show this on the right-hand side of the bar
        const filterComponent = props.filterComponent ?
                                <div key='filter-component' className="editable-table__filter-component">
                                    {props.filterComponent}
                                </div> : null;

        // Toggles are checkboxes with binary states. They will go to the right of the filter component
        const _toggles = toggles?.length > 0
                         ? <Toggles key='toggles'
                                    toggles={toggles}
                                    onClick={onClickToggle}
                                    indented={indentToggles}
                         />
                         : null;

        // Simple add button, only if there are no customAddLabels
        const simpleAdd = onAdd && !props.customAddLabels && canAdd
                          ? <Button ref={addRef}
                                    key='simple-add-button'
                                    startIcon={<Add/>}
                                    color='inherit'
                                    onClick={handleBeginAdd}
                                    autoFocus={hasAdded}
                                    disabled={!canAdd}
                                    title='Add'
                          >
                              {/* "Add" is never dropped on a phone: a bare "+" is ambiguous. Only the
                                  entity name is width-gated. */}
                              {`Add ${width > 1024 ? displayEntityName || upperEntityName : ''}`}
                          </Button>
            // <IconButton key='add-new-button'
            //               ref={addRef}
            //               type={Icons.ADD_SOLID}
            //               color='green'
            //               textColor='green'
            //               size='large'
            //               onClick={onBeginAdd}
            //               autoFocus={hasAdded}
            //               disabled={!canAdd}
            //               title={'Add'}
            //               label={`${isMobileView() ? '' : 'Add '}${width > 1024 ? displayEntityName || upperEntityName : ''}`}
            // />
                          : null;

        const customAddLabels = onAdd && props.customAddLabels ?
                                props.customAddLabels.map(c => {
                                    const onClick = c.onClick
                                                    ? () => c.onClick!()
                                                    : handleBeginAddLabel;
                                    return <CustomButton key={`CAL_${c.label}`}
                                                         id={`CAL_${c.label}`}
                                                         icon={c.icon}
                                                         data={c.param}
                                                         label={c.label}
                                                         size={c.size}
                                                         color={c.color}
                                                         title={c.title}
                                                         onClick={onClick}
                                    />;
                                }) : null;

        const elements = [simpleAdd, customAddLabels, filterComponent, _toggles].flat().filter(util.existsFilter);

        // If we have anything to show, wrap it in a div
        if (elements.length > 0) return <div className="editable-table__add">{elements}</div>;

        // Otherwise we don't need an add bar at all
        else return null;
    }, [getSelectedRows, propItems, props.filterComponent, indentToggles, onAdd, props.customAddLabels, displayEntityName, selectedRows, toggles, onClickToggle, canAdd, handleBeginAdd, hasAdded, width, handleBeginAddLabel]);

    /**
     * Helper function, takes an event and returns the source of the drag.
     *
     * @param {DragEvent} e
     * @returns {any}
     */
    const getSource = useCallback((e: DragEvent): any => {
        const sourceId = e.dataTransfer.types.find(type => type.startsWith('dnd/'))!.replace('dnd/', '');
        return propItems.find(item => getId(item) === sourceId);
    }, [propItems, getId]);

    /**
     * onDrag event, we pass the source item of the drag, as well as the event, to the parent's onDrag.
     *
     * @param {DragEvent} e
     * @param {any} source - the item being dragged
     */
    const handleDrag = useCallback((e: DragEvent, source: any) => {
        if (onDrag) onDrag(e, source);
    }, [onDrag]);

    /**
     * Start a drag event.  We set some types and data on dataTransfer object, so that we can return sensible
     * information on the other events.
     *
     * Specifically, we set this data:
     *
     * application/{entityName} is set to the ID of the source
     * dnd/{sourceId} is set to an the ID of the source
     *
     * We then call the parent's onDragStart.
     *
     * @param {DragEvent} e
     * @param {any} source - the item being dragged
     */
    const handleDragStart = useCallback((e: DragEvent, source: any) => {
        const id = getId(source);
        e.dataTransfer.setData(`application/${entityName.toLowerCase()}`, id);
        e.dataTransfer.setData(`dnd/${id}`, id);

        if (onDragStart) onDragStart(e, source);
    }, [entityName, getId, onDragStart]);

    /**
     * Calls the parent's onDragEnter with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the item being dragged
     */
    const handleDragEnter = useCallback((e: DragEvent, target: any) => {
        if (onDragEnter && isSameType(e)) {
            onDragEnter(e, getSource(e), target);
        }
    }, [isSameType, onDragEnter, getSource]);

    /**
     * Calls the parent's onDragExit with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the item being dragged
     */
    const handleDragExit = useCallback((e: DragEvent, target: any) => {
        if (onDragExit && isSameType(e))
            onDragExit(e, getSource(e), target);
    }, [isSameType, onDragExit, getSource]);

    /** Get the visible items via the shared pipeline (toggles → filters → sort → rank). */
    const sortedItems = useMemo((): T[] => {
        return applyFilterAndSortPipeline(propItems ?? []);
    }, [applyFilterAndSortPipeline, propItems]);

    // Defer the heavy row rendering to allow Suspense fallback to show
    const deferredSortedItems = useDeferredValue(sortedItems);

    // Show loading when explicitly set or width not yet measured
    // Note: We intentionally do NOT include isDataPending (sortedItems !== deferredSortedItems)
    // because that causes visible flashes when items update (e.g., moving a task to another day).
    // The deferred items are already used for rendering, so we don't need to hide them during updates.
    const isLoading = loading || width === 0;

    /**
     * Calls the parent's onDragLeave with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the item being dragged
     */
    const handleDragLeave = useCallback((e: DragEvent, target: any) => {
        if (isSameType(e)) {
            // Get all elements of the rowClass
            const els = document.getElementsByClassName(`row_${String(getId(target)).replace(/[^a-zA-Z0-9_-]/g, '_')}`) as HTMLCollectionOf<HTMLDivElement>;
            const source = getSource(e);
            const moveBackward = sortedItems.indexOf(source) > sortedItems.indexOf(target);

            if (moveBackward) Array.from(els).forEach(el => {
                el.style.borderTop = 'none';
            });
            else Array.from(els).forEach(el => {
                el.style.borderBottom = 'solid thin rgba(128, 128, 128, 0.5)';
            });

            if (onDragLeave) onDragLeave(e, getSource(e), target);
        }
    }, [isSameType, getId, getSource, sortedItems, onDragLeave]);

    /**
     * Calls the parent's onDragEnd with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the target item
     */
    const handleDragEnd = useCallback((e: DragEvent, target: any) => {
        if (onDragEnd && isSameType(e))
            onDragEnd(e, getSource(e), target);
    }, [isSameType, onDragEnd, getSource]);

    /**
     * Calls the parent's onDragOver with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the target item
     */
    const handleDragOver = useCallback((e: DragEvent, target: any) => {
        // Is this the same entity type?
        if (isSameType(e)) {
            const source = getSource(e);

            if (getId(source) !== getId(target)) {
                e.preventDefault();

                // Get all elements of the rowClass
                const els = document.getElementsByClassName(`row_${String(getId(target)).replace(/[^a-zA-Z0-9_-]/g, '_')}`) as HTMLCollectionOf<HTMLDivElement>;
                const moveBackward = sortedItems.indexOf(source) > sortedItems.indexOf(target);

                if (moveBackward) Array.from(els).forEach(el => {
                    el.style.borderTop = 'solid 1px red';
                });
                else Array.from(els).forEach(el => {
                    el.style.borderBottom = 'solid 1px red';
                });


                if (onDragOver) onDragOver(e, source, target);
            }
        }
    }, [isSameType, getSource, getId, sortedItems, onDragOver]);

    /**
     * Calls the parent's onDrop with e, source and target if this is the same entity type.
     *
     * @param {DragEvent} e
     * @param {any} target - the target item
     */
    const handleDrop = useCallback((e: DragEvent, target: any) => {
        if (isSameType(e)) {
            const source = getSource(e);
            e.preventDefault();

            // Get all elements of the rowClass
            const els = document.getElementsByClassName(`row_${String(getId(target)).replace(/[^a-zA-Z0-9_-]/g, '_')}`) as HTMLCollectionOf<HTMLDivElement>;
            const moveBackward = sortedItems.indexOf(source) > sortedItems.indexOf(target);

            if (moveBackward) Array.from(els).forEach(el => {
                el.style.borderTop = 'none';
            });
            else Array.from(els).forEach(el => {
                el.style.borderBottom = 'solid thin rgba(128, 128, 128, 0.5)';
            });

            if (onDrop) onDrop(e, source, target, moveBackward);
        }
    }, [isSameType, getSource, getId, sortedItems, onDrop]);

    /**
     * Called when __content is scrolled.  Set the scrollIndicator
     *
     * @param e
     */
        // const onEditorScroll = useCallback((e: SyntheticEvent) => {
        //     e.stopPropagation();
        //
        //     const element = document.getElementById(`editable-table__content:${propsName}`);
        //     if (!!element && (scrollTop !== element.scrollTop)) {
        //         setScrollTop(element.scrollTop);
        //     }
        // }, [propsName, scrollTop, setScrollTop]);

    const handleGridScroll = useCallback((e: SyntheticEvent) => {
            e.stopPropagation();

            if (!isEditing && gridRef.current && (gridScrollTop.current !== gridRef.current.scrollTop)) {
                gridScrollTop.current = gridRef.current.scrollTop;
            }
        }, [isEditing, gridScrollTop]);

    /**
     * If this is the selected sort column, reverse it.
     * If this is NOT the selected sort column, just select this column.
     *
     * @param {EditableTableSortEntry<{T}>} sortProp
     * @param {'asc' | 'desc'} [sortOverride] - optional override for the sort order
     */
    const handleSort = useCallback((sortProp: EditableTableSortEntry<T>, sortOverride?: 'asc' | 'desc') => {
        // Multi-column behaviour: clicking a column header (or shift-click) toggles this
        // column's entry in the sort stack. Other columns' entries are preserved — that's
        // the only difference from the single-column-sort days. If the column isn't in the
        // stack, it's added as the primary (front of the list). If it's already there, its
        // order toggles asc ↔ desc.
        //
        // Excel and Sheets append to the END (lowest priority) when shift-clicking. We
        // append to the FRONT (highest priority) — that matches "I just clicked this column,
        // I want it to be the dominant sort". The FilterDialog's Sort Manager lets users
        // reorder if they want different priority.
        setSortStack(prev => {
            const idx = prev.findIndex(e => e.label === sortProp.label);
            if (idx === -1) {
                // Not in stack — add as primary, with the supplied override or default asc.
                return [{label: sortProp.label, order: sortOverride ?? 'asc'}, ...prev];
            }
            // Already in stack — replace this entry with the override or a flipped order.
            const existing = prev[idx];
            const nextOrder = sortOverride ?? (existing.order === 'asc' ? 'desc' : 'asc');
            const updated = [...prev];
            updated[idx] = {label: existing.label, order: nextOrder};
            return updated;
        });
    }, []);

    /**
     * Call an external action.
     *
     * @param {any} oa
     * @param {<T>} item
     */
    const callAction = useCallback((oa: EditableTableOtherAction<T>, item: T) => {
        // Call the action
        oa.action(item);
    }, []);

    /**
     * Clear the sort for this column.
     *
     * @param {EditableTableSortEntry<{T}>} _
     */
    const handleClearSort = useCallback((sortProp: EditableTableSortEntry<T>) => {
        // Removes only this column's entry from the stack; other columns' sort priority is unaffected.
        setSortStack(prev => prev.filter(e => e.label !== sortProp.label));
    }, []);

    // const paged = useCallback((items: any[]) => {
    //     if (!flowStyle && items.length > pageSize) {
    //         const start = pageNumber * pageSize;
    //         const end = start + pageSize;
    //         return items.slice(start, end);
    //     }
    //     else return items;
    // }, [flowStyle, pageSize, pageNumber]);

    /**
     * Export the (sorted) contents of this table to an Excel spreadsheet.
     */
    const handleExport = useCallback(() => {
        try {
            const exportProps = props.exportProps || props.properties.filter((_, i) => i > 0);
            const currentSelectedRows = getSelectedRows(propItems, selectedRows);
            const itemCount = currentSelectedRows.length;
            const data = sortedItems
                .filter(item => itemCount
                                ? currentSelectedRows.includes(getId(item))
                                : true)
                .map(item => {
                    const xrow: XlsxRow = [];

                    props.properties
                        .filter(prop => {
                            const exportProp = exportProps.find(ep => ep.label === prop.label);
                            return !(exportProp && exportProp.doNotExport);
                        })
                        .forEach((prop, index) => {
                            const exportProp = exportProps.find(ep => ep.label === prop.label);
                            const sortProp = sortProps?.find(sp => sp.label === prop.label);

                            if ((index > 0 || (index === 0 && exportProps[0].label === props.properties[0].label))) {
                                let content: string | Date | number;

                                // flattenForCell handles arrays + ReactNode objects, mirroring
                                // extractValue's logic so Excel and filter values stay consistent
                                // (multi-value columns join with ", " not concatenate).
                                // @ts-ignore
                                if (exportProp?.useFormatted) content = flattenForCell(prop.renderItemFn(item, prop.label, true)) as any;

                                // @ts-ignore
                                else if (exportProp?.exportFn) content = flattenForCell(exportProp.exportFn(item, prop.label, true)) as any;
                                else {
                                    if (prop.renderItemFn) {
                                        content = flattenForCell(prop.renderItemFn(item, prop.label, true)) as any;
                                    }
                                    else {
                                        content = flattenForCell(item[prop.label]) as any;
                                    }
                                }

                                // Any HTML has been stripped
                                let cellValue: number | string | Date = content;
                                let type = exportProp?.type || sortProp?.type || prop.type || 'string';

                                // @ts-ignore
                                if (exportProp?.useFormatted) type = 'string';

                                // Handle empty values
                                if (!cellValue && cellValue !== 0) {
                                    if (type === 'string') cellValue = ' ';
                                    else if (type === 'number' || type === 'money') cellValue = 0;
                                }
                                xrow.push({value: cellValue, type: type as 'string' | 'number' | 'date' | 'money'});
                            }
                        });

                    return xrow;
                });

            // Add header row
            const header: XlsxRow = [];

            props.properties
                .filter(prop => {
                    const exportProp = exportProps.find(ep => ep.label === prop.label);
                    return !(exportProp && exportProp.doNotExport);
                })
                .forEach((prop, index) => {
                    const exportProp = exportProps.find(ep => ep.label === prop.label);
                    if (index > 0 || (index === 0 && exportProps[0].label === props.properties[0].label)) {
                        const displayLabel = exportProp?.displayLabel || prop.displayLabel;
                        const value = typeof displayLabel === 'string'
                            ? displayLabel
                            : upFirstLetter(splitWords(prop.label).join(' '));

                        header.push({value, type: 'string'});
                    }
                });

            data.splice(0, 0, header);

            const config = {
                filename: props.exportTitle ?? propsName,
                sheet: {
                    data
                }
            };

            exportToXlsx(config).catch((err: any) => {
                console.error(err);
                confirmWarning('Error Exporting', err.message);
            });
        }
        catch (err: any) {
            console.error(err);
            confirmWarning('Error Exporting', err.message);
        }
    }, [props.exportProps, props.properties, getSelectedRows, propItems, selectedRows, sortedItems, getId, sortProps, splitWords, props.exportTitle, propsName]);

    const getSortIcon = useCallback((sortProp: EditableTableSortEntry<T>) => {
        // The icon constants are named after their FontAwesome glyphs, which are slightly
        // counterintuitive. We pick the icon that *depicts* the current sort direction:
        //   - asc on text   → SORT_DOWN_ALPHA   (fa-sort-alpha-down, depicts A↓Z)
        //   - desc on text  → SORT_UP_ALPHA     (fa-sort-alpha-down-alt, depicts Z↓A)
        //   - asc on number → SORT_DOWN_NUMERIC (1↓9)
        //   - desc on num.  → SORT_UP_NUMERIC   (9↓1)
        //   - asc on date   → TIME_SORT_ASC, desc → TIME_SORT_DESC
        //   - asc generic   → SORT_UP    (tagged 'ascend')
        //   - desc generic  → SORT_DOWN  (tagged 'descend')
        // This must stay aligned with FilterDialog.sortIconsForKind so the column header and
        // the dialog buttons render identical icons for the same sort state.
        if (sortProp.type === 'string') return sortProp.order === 'desc' ? Icons.SORT_UP_ALPHA : Icons.SORT_DOWN_ALPHA;
        if (sortProp.type === 'number' || sortProp.type === 'money') return sortProp.order === 'desc' ? Icons.SORT_UP_NUMERIC : Icons.SORT_DOWN_NUMERIC;
        if (sortProp.type === 'date') return sortProp.order === 'desc' ? Icons.TIME_SORT_DESC : Icons.TIME_SORT_ASC;
        return sortProp.order === 'desc' ? Icons.SORT_DOWN : Icons.SORT_UP;
    }, []);

    const selectRow = useCallback((item: T) => {
        const itemId = getId(item);

        // Use functional update to avoid stale closure when clicking rapidly
        // Without this, rapid clicks would read stale selectedRows from closure
        setSelectedRows(prev => {
            const exists = prev.includes(itemId);
            return exists
                ? prev.filter(id => id !== itemId)  // Remove this item
                : [...prev, itemId];                 // Add this item
        });
    }, [getId, setSelectedRows]);

    /**
     * Call a multiple-item function.  Call the action with all of the affected items.
     *
     * @param {any} multiAction
     * @param {function} multiAction.action - a function to call - passes the item back
     * @param {string} multiAction.label - a label for the UI
     */
    const callMultiAction = useCallback((multiAction: EditableTableOtherAction<T>) => {
        // Use functional update to get current selectedRows and avoid stale closure
        setSelectedRows(currentSelectedRows => {
            const _selectedRows = getSelectedRows(propItems, currentSelectedRows);
            const items = sortedItems.filter(item => _selectedRows.includes(getId(item)));

            try {
                multiAction.action(items, () => {
                    onEndEdit?.();
                });
            }
            catch (err: any) {
                console.error(err);
                confirmWarning(`Error calling ${multiAction.label} ${displayEntityName || entityName}`, err.message);
            }

            return [];  // Clear selection after action
        });
    }, [propItems, getSelectedRows, sortedItems, getId, setSelectedRows, entityName, onEndEdit, displayEntityName]);

    const handleCheckAllNone = useCallback(() => {
        const allIds = propItems.map(item => getId(item));

        // Use functional update to avoid stale closure when toggling rapidly
        setSelectedRows(prev => {
            const selectedLength = getSelectedRows(propItems, prev).length;
            if (propItems.length > 0 && selectedLength > 1) return [];
            return allIds;
        });
    }, [getSelectedRows, propItems, getId, setSelectedRows]);

    /**
     * Handles clicks on the filter button (three vertical dots) on a column header.
     * SHIFT-clicks will treat as sorting, otherwise open the filter dialog.
     *
     * The goal is to create a FilterProp object that contains all the info to do filtering on this field,
     * then set state for filterProps (poorly named, as it is one set of props?) and showFilter.
     * This will cause the non-modal dialog to show for this field.
     *
     * @param {MouseEvent} e
     * @param {EditableTableSortEntry<{T}>} sortProp
     */
    const handleFilterClick = useCallback((e: MouseEvent, sortProp: EditableTableSortEntry<T>) => {
        e.preventDefault();

        // SHIFT-click is a fast-path sort.
        if (e.shiftKey) {
            handleSort(sortProp);
            return;
        }

        const matchingSortProp = sortProps.find(sp => sp.label === sortProp.label);
        const prop = props.properties.find(p => p.label === sortProp.label);
        if (!prop) return;

        const existingFilter = filters.find(f => f.label === sortProp.label) ?? null;
        const explicitChoices = props.filterPropChoices?.find(fp => fp.label === sortProp.label)?.values;

        // Auto-derive a unique-values checklist when the caller didn't supply explicit
        // filterPropChoices. The checklist surfaces when the column is "categorical" —
        // bounded distinct-value count with duplicates — regardless of declared type. This
        // catches lookup-driven columns (Type, Stage, Team, Client, Lead) AND small-range
        // numeric columns (Risk, Priority). Wide-open columns (Name, ID, Amount, free-form
        // descriptions) usually have hundreds-to-thousands of distinct values and exceed the
        // cap — they fall through to the operator-only UI.
        //
        // The checklist has a built-in search box, so a generous cap (200) is fine: a list
        // of 100+ entries stays usable when the user can filter to "Acme" in two keystrokes.
        const MAX_CHECKLIST_DISTINCT = 200;
        let checklistChoices: typeof explicitChoices = explicitChoices;
        let isMultiValueColumn = false;
        if (!explicitChoices) {
            const seen = new Set<string>();
            const ep = props.exportProps?.find(e => e.label === sortProp.label);
            const fn = (ep?.exportFn || prop.renderItemFn) as ExportFn<T> | undefined;
            const items = propItems ?? [];
            // Track total atomic occurrences so we still suppress the checklist on free-form
            // columns where every cell is unique. For array columns, total counts each element.
            let totalParts = 0;
            let overCap = false;
            for (const item of items) {
                // extractValueParts breaks multi-value (array) cells into individual entries,
                // so the Team column with [JMC, MR] contributes "JMC" and "MR" separately —
                // and a row with [JMC, SK] contributes "JMC" (dedup'd) and "SK". The user
                // ends up with a flat list {JMC, MR, SK} rather than the compound strings.
                const parts = extractValueParts(item, prop, fn);
                if (parts.length > 1) isMultiValueColumn = true;
                for (const p of parts) {
                    totalParts++;
                    seen.add(p);
                    if (seen.size > MAX_CHECKLIST_DISTINCT) {
                        overCap = true;
                        break;
                    }
                }
                if (overCap) break;
            }
            // Surface the checklist when:
            //   1. There are duplicates — at least two cells share a value. For array cells
            //      this means cumulative occurrences (totalParts) exceed distinct count.
            //   2. Distinct count is small enough to be useful (≤ MAX_CHECKLIST_DISTINCT).
            if (!overCap && seen.size > 0 && seen.size < totalParts) {
                checklistChoices = Array.from(seen).sort((a, b) => a.localeCompare(b));
            }
        }

        // Compute "N matches" for a draft filter — used by FilterDialog's footer.
        const matchCount = (draft: ColumnFilter | null): number => {
            const otherFilters = filters.filter(f => f.label !== sortProp.label);
            const combined = draft ? [...otherFilters, draft] : otherFilters;
            const baseItems = (propItems ?? []).filter(item => {
                if (!ignoreToggles && toggles?.length > 0) {
                    if (!toggles.every(toggle => toggle.toggleFilterFn(item, toggle.enabled))) return false;
                }
                return combined.every(filter => {
                    const sp = sortProps.find(s => s.label === filter.label);
                    const p = props.properties.find(pp => pp.label === filter.label);
                    if (!p) return true;
                    const ep = props.exportProps?.find(e => e.label === filter.label);
                    const fn = (ep?.exportFn || p.renderItemFn) as ExportFn<T> | undefined;
                    return evaluateColumnFilter(item, filter, p, sp, fn);
                });
            });
            return baseItems.length;
        };

        setFilterProps({
            label: prop.label,
            displayLabel: prop.displayLabel || prop.label,
            sortProp: matchingSortProp,
            currentFilter: existingFilter,
            checklistChoices,
            isMultiValueColumn,
            matchCount,
            totalCount: propItems?.length ?? 0,
            onDraftChange: (draft) => setDraftFilter({label: prop.label, filter: draft}),
            onSortDraftChange: (stack) => setDraftSortStack(stack),
            currentSortStack: sortStack,
            // Every column in sortProps is sortable — surface them all so the dialog's
            // Sort Manager can offer them as "Add a column..." options.
            sortableColumns: sortProps.map(sp => {
                const p = props.properties.find(pp => pp.label === sp.label);
                const display = typeof p?.displayLabel === 'string'
                    ? p.displayLabel
                    : upFirstLetter(splitWords(sp.label).join(' '));
                return {label: sp.label, displayLabel: display, kind: (sp.type ?? 'other')};
            }),
        });
        setShowFilter(true);
    }, [handleSort, filters, props.filterPropChoices, props.properties, props.exportProps, sortProps, sortStack, propItems, ignoreToggles, toggles, splitWords]);

    const handleFilterOk = useCallback((incomingFilterResult: FilterResult<T>) => {
        // Commit the dialog's draft sort stack wholesale — it has already been authored to
        // include this column's intent + any reordering / removals the user did. Empty
        // means "no sort".
        setSortStack(incomingFilterResult.sortStack ?? []);

        const label = incomingFilterResult.sortProp.label;
        const existingFiltersClone = filters ? filters.filter(f => f.label !== label) : [];
        if (incomingFilterResult.isFiltered && incomingFilterResult.filter) {
            existingFiltersClone.push(incomingFilterResult.filter);
        }
        setFilters(existingFiltersClone);
        setFilterProps({} as FilterProps<T>);
        setDraftFilter(null);
        setDraftSortStack(null);
    }, [filters, setFilters, setFilterProps]);

    const handleFilterCancel = useCallback(() => {
        setShowFilter(false);
        setFilterProps({} as FilterProps<T>);
        setDraftFilter(null);
        setDraftSortStack(null);
    }, [setShowFilter, setFilterProps]);

    // const handlePager = useCallback((pageNumber: number) => {
    //     setPageNumber(pageNumber, () => {
    //         gridRef.current?.scrollTo(0, 0);
    //     });
    // }, [setPageNumber]);
    //
    // const renderPaging = useCallback(() => {
    //     if (isEditing || isAdding) return null;
    //
    //     const numPages = Math.ceil(sortedItems.length / pageSize);
    //
    //     // Create a box grid for pages
    //     return <div className='editable-table__pager'>
    //         <Pager numPages={numPages}
    //                onChange={handlePager}
    //                selectedPage={Math.min(numPages - 1, pageNumber)}
    //         />
    //     </div>;
    // }, [isEditing, isAdding, sortedItems.length, pageSize, handlePager, pageNumber]);

    const groups = useMemo(() => {
        return props.groups?.filter(group => {
            // Get properties in this group that are visible NOW
            let items = props.properties.filter(p => p.group === group.name)
                .filter(p => {
                    if (p.hideOnMobile && isMobileView()) return false;
                    return isPropertyShown(p);
                });

            return items.length > 0;
        }) || [];
    }, [props.groups, props.properties, isPropertyShown]);


    const getGroupInfo = useCallback((groupName: string): GetGroupInfoReturn => {
        const group = groups?.find(g => g.name === groupName);
        if (group) return {
            backgroundColor: group.backgroundColor,
            textColor: group.textColor || 'black',
            help: group.help
        };
        else return {backgroundColor: 'white', textColor: 'red'};
    }, [groups]);

    /**
     * Grid headers can be grouped with labels and special colors.
     *
     * @param {EditableTablePropertyEntry[]} properties
     * @return {ReactElement[]}
     */
    const renderGridHeaderGroups = useCallback((properties: EditableTablePropertyEntry<T>[]): ReactElement[] | undefined => {
        if (groups?.length > 0) {
            // Map properties to their groups, keeping original indexes
            // Only include properties whose group exists in the groups array
            const groupMembership = properties
                .map((p, index) => ({group: p.group, index}))
                .filter(item => groups.some(g => g.name === item.group));

            // Get distinct groups with their member indexes (using original property indexes)
            const groupLabels = groupMembership
                .map(item => item.group)
                .filter(util.distinctFilter)
                .map(label => {
                    return {
                        label,
                        indexes: groupMembership
                            .filter(item => item.group === label)
                            .map(item => item.index)
                    }
                });

            let gridColumn = 1;

            const results = groupLabels.map(gl => {
                const {label, indexes} = gl;

                const {backgroundColor, textColor, help} = getGroupInfo(label as string);
                const style: CSSProperties = {};
                style.gridColumn = `${gridColumn} / span ${indexes.length}`;
                style.backgroundColor = chroma(backgroundColor).darken(.35).hex();
                style.color = textColor;

                const el = (
                    <div key={`grid-group-${label}`}
                         className='editable-table__header-group'
                         style={style}>{label}
                    </div>);

                gridColumn = gridColumn + indexes.length;

                if (help) {
                    return <div key={`grid-group-with-help-${label}`}
                                className='editable-table__header-group-with-help'
                                style={style}>
                        {el}
                        <Help key={`grid-group-help-${label}`} title={label} message={help}/>
                    </div>
                }

                else return el;
            });

            if (props.exportProps) results.push(<div key={`grid-group-export`}
                                                     className='editable-table__header-group-with-help'
                                                     style={{backgroundColor: 'gray', color: 'white'}}/>);

            return results;
        }
    }, [props.exportProps, groups, getGroupInfo]);

    /**
     * Render the grid headers, cells and footers.
     *
     * @param {boolean} editing
     * @param {CSSProperties} style
     * @param {boolean} areAllChecked
     * @param {EditableTablePropertyEntry[]} properties
     * @param {boolean} anyButtons
     * @param {<T>[]} gridItems
     * @param {number[]} selectedRows
     * @param {string} footerClass
     * @param {string} footerCellClass
     * @returns {ReactElement}
     */
    const renderGrid = (
        editing: boolean,
        style: CSSProperties,
        areAllChecked: boolean,
        properties: EditableTablePropertyEntry<T>[],
        anyButtons: boolean,
        gridItems: T[],
        selectedRows: string[],
        footerClass: string,
        footerCellClass: string
    ): ReactElement | null => {
        const rotation = rotateLabels ? 'rotate' : '';
        let classes = ['editable-table__grid'];
        if (!hideGridOnEdit && editing) classes.push('editable-table--grid-hidden');
        if (groups?.length > 0) classes.push('editable-table--grouped');
        const name = propsName;

        // Set CSS variables for rotated labels
        const gridStyle: CSSProperties = {...style};
        if (rotateLabels) {
            gridStyle['--rotation-angle' as string] = `${rotationAngle}deg`;
            if (rotationLength) {
                gridStyle['--rotate-border-width' as string] = `${rotationLength}em`;
            }
        }

        if ((hideGridOnEdit && editing) || (hideHeadersIfNoData && gridItems.length === 0)) return null;
        else return <div
            className={classes.join(' ')}
            style={gridStyle}
            ref={gridRef}
            onScroll={handleGridScroll}
        >
            {/* Multi-Select Button */}
            {!editing && multiSelect &&
				<div key='multi_select_header'
				     data-col-label="__checkbox__"
				     className={`editable-table__header-col editable-table--center`}
				>
					<IconButton type={areAllChecked ? Icons.CHECKED : Icons.UNCHECKED}
					            title='Select All/None'
					            onClick={handleCheckAllNone}
					/>
				</div>
            }

            {/* Grid Header Groups */}
            {!editing && renderGridHeaderGroups(properties)}

            {/* Grid Header */}
            {!editing && properties.filter(p => !p.exportOnly)
                .map((header, propIndex) => {
                    // effectiveSortProps splices the stack's `order` into each entry. Using it
                    // here (rather than the raw sortProps state) is what makes the header icon
                    // flip between asc / desc as the user toggles sort — sortProps[i].order is
                    // never mutated after the move to multi-column sortStack.
                    const sortProp = effectiveSortProps ? effectiveSortProps.find(sp => sp.label === header.label) : null;
                    // let sortIcon = null;
                    let filterIcon: ReactElement | null = null;
                    let sortFn: (() => void) | undefined = undefined;

                    if (sortProp) {
                        // Color codes for icon:
                        // - blue if sorted
                        // - green if filtered
                        // - purple is sorted and filtered
                        const filter = filters.find(filter => filter.label === sortProp.label);
                        // Any entry in the sort stack — primary or secondary — counts as sorted.
                        const sorted = sortStack.some(e => e.label === sortProp.label);
                        const filtered = !!filter && (
                            (filter.conditions && filter.conditions.length > 0) ||
                            (filter.boolValue !== undefined && filter.boolValue !== 'either') ||
                            (filter.checklist && filter.checklist.length > 0)
                        );
                        const filterColor =
                            sorted ? filtered ? // sorted
                                     'maroon' : // sorted and filtered
                                     'blue' :  // sorted but not filtered
                            filtered ? // not sorted
                            'red' :  // not sorted but filtered
                            'black'; // not sorted or filtered

                        sortFn = () => handleSort(sortProp);
                        filterIcon = <CustomButton
                            id={`ET_SORT_${sortProp.label}`}
                            icon={sorted && filtered ? Icons.SORT_FILTER : sorted ? getSortIcon(sortProp) : filtered ? Icons.FILTER : Icons.ELLIPSES_VERTICAL}
                            data={sortProp}
                            label={''}
                            color={filterColor}
                            size={Icons.IconSizeSmall}
                            title='Click to filter, <shift>-Click to sort'
                            onClick={handleFilterClick}
                        />
                    }

                    const justify = header.justify ? `justify-${header.justify}` : '';
                    const sortClass = sortFn ? 'editable-table--sortable' : '';
                    const style: CSSProperties = {zIndex: properties.length - propIndex};

                    if (header.group) {
                        const {backgroundColor, textColor} = getGroupInfo(header.group);
                        style.backgroundColor = backgroundColor;
                        style.color = textColor;
                    }

                    return (
                        <div key={header.label}
                             style={style}
                             data-col-label={header.label}
                             className={`editable-table__header-col editable-table--${justify || 'left'} ${sortClass} ${rotation}`}>
                            <div className={`editable-table__header-col-text`}>
                                <div>{header.hideLabel
                                      ? ''
                                      : width < (smallWidth || 800) && header.displayLabelSmall
                                        ? header.displayLabelSmall
                                        : header.displayLabel || upFirstLetter(splitWords(header.label).join(' '))}
                                </div>
                            </div>
                            {filterIcon &&
								<span className='editable-table__header-col-icon-filter'>{filterIcon}</span>}
                        </div>
                    );
                })}

            {/* Action buttons */}
            {!editing && (gridItems.length > 0) && (anyButtons || props.exportProps) &&
				<div key="ButtonActions" data-col-label="__buttons__" className="editable-table__header-col Buttons">
					<IconButton type={Icons.FILE_XLS}
					            color='green'
					            onClick={handleExport}
					            title='Export to Excel'
					/>
				</div>}

            {/* Items - one row each */}
            {!editing && !isLoading && gridItems.map((item: T, rowIndex: number) => {
                const itemId = getId(item);
                const evenOdd = (rowIndex % 2) ? 'editable-table--odd' : 'editable-table--even';
                const bgStyle = rowStyleFn?.(item, rowIndex, properties.length) || {};

                return (
                    <EditableTableRow
                        key={itemId}
                        item={item}
                        rowIndex={rowIndex}
                        properties={properties}
                        itemId={itemId}
                        entityName={entityName}
                        tableName={name}
                        isSelected={selectedRows.includes(itemId)}
                        isDeleting={isDeleting}
                        viewOnly={viewOnly}
                        editing={editing}
                        width={width}
                        canEdit={(onEdit && (canEditFn?.(item) ?? true)) as boolean}
                        canDelete={(onDelete && (canDeleteFn?.(item) ?? true)) as boolean}
                        canDeleteMessage={canDeleteMessageFn?.(item)}
                        multiSelect={multiSelect as boolean}
                        isDraggable={(isDraggable && (canEditFn?.(item) ?? true)) as boolean}
                        hideStandardActions={hideStandardActions as boolean}
                        showReadOnlyIcon={showReadOnlyIcon as boolean}
                        evenOdd={evenOdd}
                        bgStyle={bgStyle}
                        rowStyleFn={rowStyleFn}
                        otherActions={props.otherActions}
                        onBeginEdit={handleBeginEdit}
                        onDelete={handleDelete}
                        onSelectRow={selectRow}
                        onCallAction={callAction}
                        onDragStart={handleDragStart}
                        onDragEnter={handleDragEnter}
                        onDragExit={handleDragExit}
                        onDragLeave={handleDragLeave}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrag={handleDrag}
                        onDrop={handleDrop}
                        anyButtons={anyButtons}
                    />
                );
            })}

            {/* Footer, if any footer columns */}
            {(!editing && (props.sumProps?.length ?? 0) > 0) && multiSelect && (gridItems.length > 0) &&
				<div key={`Footer-checkmark`} className={footerClass}>
					<div className={footerCellClass}/>
				</div>
            }

            {!editing && (props.sumProps?.length ?? 0) > 0 && gridItems.length > 0 &&
                properties.map((prop) => {
                    const sumProp = props.sumProps!.find(sumProp => sumProp.label === prop.label);

                    // Found a footer column
                    if (sumProp) {
                        if (sumProp.renderItemFn) return <div key={`Footer-${sumProp.label}`} className={footerClass}>
                            <div className={footerCellClass}>
                                {sumProp.renderItemFn(gridItems)}
                            </div>
                        </div>;

                        else {
                            const isMoney = sumProp.type === 'money';
                            let total: number = MathUtil.sumByField(gridItems, sumProp.label, isMoney, isMoney ? 2 : null);
                            if (sumProp.type === 'money') total = util.formatCurrency(total, sumProp.textOnly, sumProp.noCents, 2) as unknown as number;

                            return <div key={`Footer-${prop.label}`} className={footerClass}>
                                <div className={footerCellClass}>{total}</div>
                            </div>;
                        }
                    }
                    else return <div key={`Footer-${prop.label}`} className={footerClass}>
                        <div className={footerCellClass}/>
                    </div>;
                })
            }

            {/* Footer Line */}
            {!editing && (props.sumProps?.length ?? 0) > 0 && gridItems.length > 0 && anyButtons &&
				<div className={footerClass}>
					<div className={footerCellClass}/>
				</div>
            }
        </div>;
    }

    /**
     * Render the EditableTable.
     *
     * Testing out a new virtualized container that only shows enough rows to fit on the screen.
     * We need to know:
     * - The height of the EditableTable
     */
    const properties = useMemo(() => props.properties
            .filter(p => !p.exportOnly)
            .filter(p => !p.hide && !(mobile && p.hideOnMobile))
            .filter(p => isPropertyShown(p)),
        [props.properties, mobile, isPropertyShown]);

    const Editor = props.editor || EditorCard;


    const editing = isAdding || isEditing || !!startWith;
    const hasButtons = !!(
        onAdd ||
        onEdit ||
        onDelete ||
        props.otherActions?.length ||
        props.exportProps
    );
    const anyButtons = !editing && hasButtons;

    // Internal-column widths (checkbox + action buttons) are intrinsic-
    // sized (`max-content`); lock to measured pixels so they don't
    // re-resolve when content changes.  These two columns never use
    // `fr` units — they always carry icon/button content that has a
    // natural width, so pixel-locking is correct here.  Mobile keeps
    // the original `minmax(min-content, max-content)` form so the
    // action column can compress under extreme constraint.
    const checkboxMeasured = measuredWidths?.['__checkbox__'];
    const checkboxColumn = checkboxMeasured != null ? `${checkboxMeasured}px` : 'max-content';

    const buttonsMeasured = measuredWidths?.['__buttons__'];
    const actionColumn = anyButtons
                         ? mobile
                           ? 'minmax(min-content, max-content)'
                           : buttonsMeasured != null
                             ? `${buttonsMeasured}px`
                             : 'max-content'
                         : '';

    // We assume that if a width is not stated, it will be 100px
    // const widths = properties.map((p, i) => {
    //     const prevLabel = i === 0
    //                       ? multiSelect ? 'checkbox-end' : ''
    //                       : properties[i - 1].label + '-end';
    //
    //     const label = `[${prevLabel ? prevLabel + ' ' : ''}${p.label}-start]`;
    //
    //     return `${label} ` + (p.width ? '' + p.width : 'max-content');
    // }).join(' ') + ' [actions-start]';
    //
    // const style = properties.length > 1 ?
    //     {gridTemplateColumns: `${widths} ${actionColumn} [actions-end end]`.replaceAll('  ', ' ')} :
    //     {gridTemplateColumns: `repeat(max-content, 2) [actions-end end]`.replaceAll('  ', ' ')};
    //
    // if (multiSelect) {
    //     style.gridTemplateColumns = '[start checkbox-start] max-content ' + style.gridTemplateColumns;
    // }

    // Build grid-template-columns from each column's declared width +
    // measured pixel value.  See `buildGridColumnTrack` for the rule.
    const widths = properties
        .map(p => buildGridColumnTrack(p.width, measuredWidths?.[p.label]))
        .join(' ');
    // Columns whose track takes the measured width; only their content can outgrow it.
    const measuredLabels = new Set(properties
        .filter(p => buildGridColumnTrack(p.width, 1) !== buildGridColumnTrack(p.width, undefined))
        .map(p => p.label));
    const style = properties.length > 1 ?
        {gridTemplateColumns: `${widths} ${actionColumn}`} :
        {gridTemplateColumns: `repeat(max-content,2)`};

    if (multiSelect) {
        style.gridTemplateColumns = `${checkboxColumn} ` + style.gridTemplateColumns;
    }

    // Stable cache key — changes when the set of visible columns
    // changes (different labels, multiSelect toggle, or whether the
    // table has action buttons at all), invalidating any previous
    // measurement.  Two renders with the same visible-column set
    // produce string-equal keys, so the measurement effect (gated on
    // `measureCacheKey`) doesn't re-run for filter / sort / data
    // updates within a stable layout.
    //
    // OWA-1026: the key uses `hasButtons`, not `anyButtons`.  Opening an
    // editor drops the action column, but the grid is not rendered at all
    // while editing (`renderGrid` is called only inside `<If test={!editing}>`
    // in the render below), so that narrower column set is never on screen.
    // Keying on it made opening and closing an editor two column-set changes,
    // and each close measured every row through renderToString before the
    // table could paint: about a second on the Invoices page.  If the grid
    // ever stays rendered while editing, this key has to follow the action
    // column again.
    const columnKey = useMemo(
        () => properties.map(p => p.label).join('|') + `|ms:${multiSelect}|ab:${hasButtons}`,
        [properties, multiSelect, hasButtons]);

    /**
     * Deterministic column width measurement.
     *
     * Runs after every render where `sortedItems` or the visible column set
     * has changed.  Hydrates from sessionStorage cache first (so revisits to
     * the same table are instant), then re-measures every row with the
     * canvas-based `measureColumnWidths` and applies any differences.
     *
     * The equality check on the state setter prevents render loops:
     * setMeasuredWidths bails out early if the result is identical to the
     * current state, so a stable dataset re-measures exactly once.
     */
    const measureCacheKey = useMemo(
        () => buildCacheKey(propsName, columnKey, props.measurementKey ?? ''),
        [propsName, columnKey, props.measurementKey],
    );

    // When the cache key changes (column set OR caller-supplied
    // measurementKey changed — e.g. a filter swap), drop the in-memory
    // widths so the growth-only logic below doesn't pin shrunk columns
    // at their previous wider values. The sessionStorage entry for the
    // NEW key is hydrated by the measurement effect on the same render.
    //
    // ORDER MATTERS: this effect MUST be declared before the measurement
    // useLayoutEffect below. React runs useLayoutEffects in declaration
    // order, so on a key change the widths are nulled first, then the
    // hydrate-and-measure effect re-fills them from the new key's cache.
    // Swap the order and the hydrate would run first, populating widths
    // that we'd then null out — leaving the table without widths for one
    // render. Keep this effect ahead of the next useLayoutEffect.
    const prevMeasureCacheKey = useRef<string>(measureCacheKey);
    useLayoutEffect(() => {
        if (prevMeasureCacheKey.current !== measureCacheKey) {
            prevMeasureCacheKey.current = measureCacheKey;
            setMeasuredWidths(null);
        }
    }, [measureCacheKey]);

    useLayoutEffect(() => {
        // 1. Cache hydration — covers the first-paint window before measurement runs.
        const cached = getCachedWidths(measureCacheKey);
        if (cached) {
            setMeasuredWidths(prev => widthsEqual(prev, cached.widths) ? prev : cached.widths);
        }

        // 2. Measure deterministically against every row.
        const grid = gridRef.current;
        if (!grid) {
            // Nothing measured: let the content check fire once the grid returns.
            measuredContentRef.current = {};
            return;
        }
        // Recorded before the early returns too, or the content check
        // below would ask for a measurement this effect then skips, on
        // every render.
        measuredContentRef.current = contentSignature(grid, measuredLabels);
        if (sortedItems.length === 0) return;

        const sampleCell = grid.querySelector(
            '.editable-table__cell:not(.editable-table__header):not(.editable-table__footer)',
        ) as HTMLElement | null;
        if (!sampleCell) return;

        const cs = getComputedStyle(sampleCell);
        const fontSize = cs.fontSize || '14px';
        const fontFamily = cs.fontFamily || 'sans-serif';
        const fontWeight = cs.fontWeight || 'normal';
        const font = `${fontWeight} ${fontSize} ${fontFamily}`;
        const paddingX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);

        const measureCols: MeasureColumn<T>[] = properties
            .filter(p => !p.width)
            .map(p => ({label: p.label}));

        // Note: don't early-return on `measureCols.length === 0`.  Even when
        // every column has an explicit `width`, the renderItemFn / header /
        // body floors below still need to populate measuredWidths so the
        // gridTemplateColumns build can apply intrinsic-sizing (max-content,
        // min-content) and minmax-flex (1fr) columns with measured floors.

        const result = measureColumnWidths(sortedItems, measureCols, {font, paddingX});

        // For columns whose cells go through `renderItemFn`, the canvas
        // measurement of `String(item[label])` misses the rendered output
        // entirely — Date objects render as "object", arrays render as
        // [object Object], and icons + formatted strings don't appear in
        // the raw field at all.  Render each row's renderItemFn output
        // through renderToString into an offscreen container, then read
        // the resulting elements' scrollWidth to get the real max per
        // column.  Override the canvas number with this when bigger.
        const renderItemFnWidths = measureRenderItemFnWidths(sortedItems, properties, sampleCell, grid);
        for (const [label, w] of Object.entries(renderItemFnWidths)) {
            if (w > (result.widths[label] ?? 0)) {
                result.widths[label] = w;
            }
        }

        // Floor each measured column at the natural width of its actual
        // header cell (contentExtent captures text + sort/filter icons + any
        // custom decoration).  Without this, columns whose body cells are
        // empty / null / object-valued (e.g. arrays consumed by
        // renderItemFn) would shrink below the header text and the header
        // would overflow into adjacent columns.
        //
        // Explicit-width columns are NOT skipped here — `max-content` /
        // `min-content` / `auto` declarations use the measured pixel value
        // directly downstream, so the header's contribution still has to
        // be folded in.
        const headerCells = grid.querySelectorAll<HTMLElement>('.editable-table__header-col');
        let cellIdx = multiSelect ? 1 : 0;
        for (const p of properties) {
            const cell = headerCells[cellIdx];
            if (cell) {
                // Content extent, not scrollWidth: a header in a flexible column stretches
                // to the track width and would otherwise pin the column there for good.
                const headerW = contentExtent(cell);
                if (headerW > (result.widths[p.label] ?? 0)) {
                    result.widths[p.label] = headerW;
                }
            }
            cellIdx++;
        }

        // Floor each measured column at the natural width of its actual
        // visible body cells, identified by `data-col-label`.  Offscreen
        // renderToString measurement can be ~1px short of the real cell
        // because class-based CSS (font, icon size, padding) on the live
        // grid doesn't always replicate exactly on the offscreen clone.
        // The actual rendered DOM is the ground truth for "what width does
        // this column need" — sample it directly to lock the floor.
        const bodyCells = grid.querySelectorAll<HTMLElement>(
            '.editable-table__cell[data-col-label]:not(.editable-table__header):not(.editable-table__footer)',
        );
        const bodyMaxByLabel = new Map<string, number>();
        for (const cell of bodyCells) {
            const label = cell.dataset.colLabel;
            if (!label) continue;
            // Content extent, not scrollWidth (see contentExtent.ts): the box of a cell in
            // a flexible column is the track width, which is the answer we are computing.
            const w = contentExtent(cell);
            const prev = bodyMaxByLabel.get(label) ?? 0;
            if (w > prev) bodyMaxByLabel.set(label, w);
        }
        for (const [label, w] of bodyMaxByLabel) {
            if (w > (result.widths[label] ?? 0)) {
                result.widths[label] = w;
            }
        }

        // Growth-only: widths can grow but never shrink while the column
        // set is unchanged.  Filter / sort changes that produce a smaller
        // measurement (because the new visible-row sample has shorter
        // content) MUST NOT shrink the column — that's the user-visible
        // flicker.  When the column set itself changes, the cache key
        // changes too and the previous state is dropped.
        setMeasuredWidths(prev => {
            if (!prev) return result.widths;
            const next: Record<string, number> = {...prev};
            let changed = false;
            for (const label of Object.keys(result.widths)) {
                const w = result.widths[label];
                const prevW = prev[label] ?? 0;
                if (w > prevW) {
                    next[label] = w;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });

        setCachedWidths(measureCacheKey, {
            widths: result.widths,
            measuredAt: result.measuredAt,
        });

        // Dependencies deliberately exclude `sortedItems` AND `properties`:
        //
        //   - `sortedItems` was the original flicker source: re-running
        //     on every filter / sort / data update produced different
        //     visible-row samples and the state setter applied the
        //     differences as visible width changes.
        //
        //   - `properties` is a fresh array reference every time the
        //     `isPropertyShown` callback's ref changes — which is every
        //     `hostWidth` change.  Depending on it would re-run the
        //     expensive measurement (canvas + offscreen renderToString
        //     batch) on every scroll/resize tick, even when the filtered
        //     set is unchanged.  `measureCacheKey` is content-based
        //     (string equality on the column-label sequence) and is the
        //     correct gate.
        //
        // The closure still reads the latest `sortedItems` and
        // `properties` when the effect does run.
        //
        // `remeasureCount` re-runs it when a measured column's content
        // grows: a `renderItemFn` can draw from data that loads after the
        // rows (Contacts' emails and phones), and growth-only measurement
        // keeps that repeat from shrinking anything.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measureCacheKey, multiSelect, remeasureCount]);

    // After every render, measure again if a measured column's cells now
    // hold more than they did at the last measurement.  Reads the DOM tree
    // only, no layout; see `contentSignature.ts`.
    //
    // No dependency list on purpose: the data a `renderItemFn` draws from
    // is not a prop of this table, so "after every render" is the only
    // point that sees it arrive.  It cannot loop: the measurement records
    // the signature it ran against, so the same content never asks twice.
    // Until widths are pinned (measured or cached), every track is still
    // intrinsic CSS and already fits its content, so there is nothing to
    // correct and no reason to pay for a measurement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid || !measuredWidths) return;
        if (contentGrew(measuredContentRef.current, contentSignature(grid, measuredLabels))) {
            setRemeasureCount(n => n + 1);
        }
    });

    // If no displayName is given, this will be used
    const upperEntityName = upFirstLetter(splitWords(entityName).join(' '));

    const addBar = getAddBar(upperEntityName);

    // If we are in multi-select mode, we need to keep track of selected items
    const previousSelectedRows = getSelectedRows(sortedItems, selectedRows) || [];

    const multiActions: ReactElement[] = [];

    // Only add delete if onDelete is available and all the rows can be deleted
    const canDeleteItem = (item: T) => canDeleteFn ? canDeleteFn(item) : true;

    // Resolve the selected ids to their live items. getSelectedRows() already filters
    // selectedRows to ids present in sortedItems, so in steady state every id resolves;
    // the .filter() narrows away the `T | undefined` from .find() soundly (no `!`) and
    // keeps this render from crashing should an id ever fail to resolve (e.g. a getId
    // that isn't stable/unique — see the dev-mode uniqueness check next to getId). (OWA-600)
    const selectedItems = previousSelectedRows
        .map(id => sortedItems.find(item => getId(item) === id))
        .filter((item): item is T => item !== undefined);

    if (selectedItems.length > 0
        && (onDeleteMany || onDelete)
        && !isDeleting
        && selectedItems.every(item => canDeleteItem(item))) {

        if (canDeleteManyFn && canDeleteManyFn(selectedItems)) {
            multiActions.push(<CustomButton id={`EditableTable-${entityName}-DeleteMany`}
                                            key={`EditableTable-${entityName}-DeleteMany`}
                                            icon={Icons.DELETE}
                                            data={selectedItems}
                                            onClick={handleDeleteMany}
                                            title="Delete"
                                            label="Delete All"
                />
            )
        }
    }

    // Show multi-actions (if any)
    if (props.otherActions && previousSelectedRows.length > 0) {
        multiActions.push(...props.otherActions
            .filter(oa => {
                const items = sortedItems.filter(item => previousSelectedRows.includes(getId(item)));

                return oa.multiOnly
                    && multiSelect
                    && (previousSelectedRows.length > 0)
                    && (oa.showIf ? oa.showIf(items) : true);
            })
            .map((oa) => {
                    return <CustomActionButton entityName={entityName}
                                               key={`EditableTable-multi-actions-${entityName}-${oa.label}`}
                                               icon={oa.selectedIcon as IconType}
                                               item={null}
                                               oa={oa}
                                               onClick={callMultiAction}
                    />;
                }
            )
        );
    }

    const areAllChecked = sortedItems?.length > 0 && sortedItems.length === previousSelectedRows.length;
    const footerClass = 'editable-table__cell editable-table__footer';
    const footerCellClass = 'editable-table__wrapper editable-table--justify-right';

    // If we are paging, we should turn off the auto-scrolling on the content and use the scrolling within the pager
    // const pagingStyles: CSSProperties = {};

    // Paging has been disabled, as we now have infinite scroll
    // const isPaging = false; // sortedItems.length > pageSize;
    // if (isPaging && !(isEditing || isAdding)) pagingStyles.overflowY = 'visible';

    const filterDialogLabel = filterProps
                              ? typeof (filterProps.displayLabel) !== 'string'
                                ? upFirstLetter(filterProps.label as string)
                                : upFirstLetter(filterProps.displayLabel as string)
                              : "";
    return (
        <Suspense fallback={<Loading zoom={10} center={true}/>}>
            <div className={`editable-table ${flowStyle ? 'editable-table--flow' : ''}`}>

                {/* Filter Dialog — the FilterDialog component owns its own header and footer chrome. */}
                {!editing &&
					<Dialog open={showFilter}
					        onClose={() => {
					            setShowFilter(false);
					            setDraftFilter(null);
					            setDraftSortStack(null);
					        }}
					        aria-labelledby={FILTER_DIALOG_TITLE_ID}
					        // A dialog taller than the screen scrolls as a whole; its footer is
					        // sticky so Apply stays on screen (OWA-942, FilterDialog.less).
					        scroll="body"
					        // The Paper (MUI's role="dialog" element) hosts FilterDialog's dropdown
					        // menus so assistive technology treats them as part of the dialog. On
					        // narrow screens, where MUI shrinks the Paper, it keeps 8px margins so the
					        // phone-width dialog fits (OWA-942).
					        slotProps={{
					            paper: {
					                id: FILTER_DIALOG_MENU_PORTAL_ID,
					                sx: (theme) => ({
					                    overflow: 'visible',
					                    [theme.breakpoints.down(theme.breakpoints.values.sm + 64)]: {
					                        margin: '8px',
					                        maxWidth: 'calc(100% - 16px)',
					                    },
					                }),
					            },
					        }}
					>
						<DialogContent style={{padding: 0, overflow: 'visible'}}>
							<FilterDialog ref={filterDialogRef}
							              onOk={(result) => {
							                  setShowFilter(false);
							                  handleFilterOk(result);
							              }}
							              onCancel={() => {
							                  setShowFilter(false);
							                  handleFilterCancel();
							              }}
							              label={filterProps?.label ?? ''}
							              displayLabel={filterDialogLabel}
							              sortProp={filterProps?.sortProp as EditableTableSortEntry<unknown>}
							              currentFilter={filterProps?.currentFilter ?? null}
							              checklistChoices={filterProps?.checklistChoices}
							              isMultiValueColumn={filterProps?.isMultiValueColumn}
							              matchCount={filterProps?.matchCount}
							              totalCount={filterProps?.totalCount}
							              onDraftChange={filterProps?.onDraftChange}
							              onSortDraftChange={filterProps?.onSortDraftChange}
							              currentSortStack={filterProps?.currentSortStack ?? []}
							              sortableColumns={filterProps?.sortableColumns ?? []}
							/>
						</DialogContent>
					</Dialog>
                }

                <ErrorBoundaryO uniqueKey={`editable-table-${propsName}`}>
                    <div ref={contentRef}
                         id={`editable-table__content:${propsName}`}
                         className={`editable-table__content`}
                        // onScroll={onEditorScroll}
                        //  style={pagingStyles}
                    >
                        {/* Editor - Only need to show the editor if editing */}
                        <ErrorBoundaryO uniqueKey={`editable-table-editor-${propsName}`} shouldUseErrorFrame={true}>
                            <AnimatePresence>
                                <If test={editing}>
                                    <motion.div className={'editable-table__add-item'}
                                                initial={{opacity: 0}}
                                                animate={{opacity: 1}}
                                                exit={{
                                                    opacity: 0,
                                                    transition: {duration: .2}
                                                }}
                                    >
                                        <Editor key={isAdding ? newId : (getCurrent() ? getId(getCurrent()!) : undefined)}
                                                current={isAdding ? null : getCurrent()}
                                                presets={props.presets}
                                                newId={newId}
                                                keys={sortedItems.map(item => item[uniqueKey!])}
                                                dropEvent={props.dropEvent}
                                                editable={editing}
                                                embedded={embedded}
                                                env={props.env || {}}
                                                onOk={isAdding ? handleAdd : handleEdit}
                                                onCancel={isAdding ? handleCancelAdd : handleCancelEdit}
                                                viewOnly={viewOnly}
                                                {...(props.editorProps || {})}
                                                isAutosave={props.editorProps?.isAutosave}
                                                param={param}
                                        />
                                        <div className='editable-table__add-item-footer'/>
                                    </motion.div>
                                </If>
                            </AnimatePresence>

                            {/* Support for custom Add methods */}
                            <If test={!editing}>{addBar}</If>

                        {/* Multi Actions */}
                        <If test={!editing && multiSelect && multiActions.length > 0}>
                            <div className='editable-table__multi-actions'>
                                <div className='editable-table__multi-actions-title'>Apply to Selected items</div>
                                <div className='editable-table__multi-actions-items'>{multiActions}</div>
                            </div>
                        </If>

                        {/* Show Multi Actions if rows selected */}
                        <If test={(!editing && multiSelect && previousSelectedRows.length > 0 && multiActions.length === 0)}>
                            <div className='editable-table__multi-actions'>
                                <div className='editable-table__multi-actions-warning'>&#x26A0;</div>
                                <div className='editable-table__multi-actions-title'>
                                    {previousSelectedRows.length > 1 ? canDeleteMessage || 'Incompatible items' : 'Select more items'}
                                </div>
                            </div>
                        </If>

                        {/* If we are paged, put the paging here */}
                        {/*<If test={isPaging && !editing}>*/}
                        {/*    <div className='editable-table__paged'>*/}
                        {/*        {renderGrid(editing, style, areAllChecked, properties, anyButtons, paged(deferredSortedItems), previousSelectedRows, footerClass, footerCellClass)}*/}
                        {/*        {renderPaging()}*/}
                        {/*    </div>*/}
                        {/*</If>*/}
                        {/*<If test={(!(isPaging && !editing)}>*/}
                        <If test={!editing}>
                            {renderGrid(editing, style, areAllChecked, properties, anyButtons, deferredSortedItems, previousSelectedRows, footerClass, footerCellClass)}
                        </If>
                        </ErrorBoundaryO>
                    </div>
                </ErrorBoundaryO>
            </div>
        </Suspense>
    );
});

export default debounceRender(EditableTable, 50);
